/* eslint-disable */
// =============================================================================
// Lead forms: SHARED LOGIC (SPEC.md §B.7). DO NOT EDIT LOCALLY. Owner: WP-01.
// Identical copies, next to leadform.contract.ts:
//   BE    src/modules/v1/lead-forms/contract/leadform.logic.ts   (spec: leadform.logic.spec.ts)
//   ADM   src/@types/leadform/leadform.logic.ts
//   FE    src/types/leadform/leadform.logic.ts                    (test: leadform.logic.test.ts)
// Check: docs/lead-forms-work/tools/check-triple.sh (each hash 3 times).
// One implementation of: content defaults, templates and normalisation (sections, thank-you buttons, header facts:
// SPEC N.1), the shape and readiness gates, the structure/content hashes, variant assignment, answer validation and
// snapshots, the single-page progress/section/issue helpers, contact extraction, URLs, attribution sanitising, the
// CSV cell guard, the funnel maths (step and one-page layouts) and the A/B maths. General forms (SPEC O.1): the
// form type, the general header, its URLs / identity / stash key, its defaults and its readiness lists. Pay once per
// person (SPEC-P.1): the mobile phone rule, the optional-email rule, dedup keys, merge helpers and edit-route helpers.
// Pure TypeScript: the only import is './leadform.contract'; no clock read (`now` is passed in), no random source
// (`rand` is passed in), no locale formatting API, no Node or browser API.
// ES5-safe for the admin's `target: es5`: no array/string "includes", no Object "values"/"entries", no Set/Map
// iteration, no 32-bit "imul" and no for-of loops (the spec greps for each of them).
// =============================================================================
import {
  BIGGEST_DROP_MIN_REACHED,
  BOT_UA_RE,
  CHOICE_TYPES,
  CONSENT_MODES,
  CTA_ID_RE,
  DATE_RE,
  DEFAULT_PHONE_COUNTRY,
  DEFAULT_SECTION_ID,
  E164_RE,
  EMAIL_RE,
  FACT_ID_RE,
  FORM_KEY_RE,
  FUNNEL_BUCKETS_MS,
  GENERAL_FACT_ICONS,
  GENERAL_STASH_PREFIX,
  GO_PRIORITY_KEYS,
  HEADER_FACT_KEYS,
  LIMITS,
  LINK_PREVIEW_UA_RE,
  META_AD_UTM,
  MOBILE_NSN,
  OPTION_ID_RE,
  PROD_SITE,
  QID_RE,
  QUESTION_TYPES,
  READINESS_GENERAL_INFO_IDS,
  READINESS_GENERAL_RECOMMENDED_IDS,
  READINESS_GENERAL_REQUIRED_IDS,
  READINESS_INFO_IDS,
  READINESS_RECOMMENDED_IDS,
  READINESS_REQUIRED_IDS,
  RESERVED_FORM_KEYS,
  RESERVED_SHORT_CODES,
  SECTION_ID_RE,
  SHORT_CODE_ALPHABET,
  SHORT_CODE_RE,
  SHORT_LINK_LIMITS,
  SHORT_LINK_TAG_PREFIX,
  SHORT_TAG_RE,
  SRM_P_THRESHOLD,
  THANKS_CTA_TYPES,
  UUID_V4_RE,
  VARIANT_KEY_RE,
} from './leadform.contract';
import type {
  AbInputRow,
  AbVariantRow,
  AbVerdict,
  AnswerError,
  AnswerIssue,
  AnswerValue,
  ApplyFacts,
  ChoiceOption,
  ConsentMode,
  CtaProblem,
  CtaProblemField,
  DedupKey,
  DedupKind,
  DoneStashLike,
  EditStashLike,
  FormProgress,
  FormReadiness,
  FormType,
  FunnelAgg,
  FunnelSectionRow,
  FunnelStepAgg,
  FunnelStepRow,
  GeneralFact,
  GeneralFactIcon,
  GeneralHeader,
  HeaderFactKey,
  LeadAnswer,
  LeadAttribution,
  LeadChange,
  LeadContact,
  LeadFormCommon,
  LeadFormContent,
  LeadFormQuestion,
  LeadFormSection,
  LeadFormVariant,
  PageIssue,
  QuestionType,
  QuitSessionLike,
  ReadinessCheck,
  ReadinessContext,
  SectionState,
  ShortLinkPath,
  ShortLinkTags,
  SrmResult,
  ThanksCta,
  ThanksCtaType,
  TwoProportionResult,
  ValidateAnswersResult,
} from './leadform.contract';

// ---- small helpers -------------------------------------------------------------------------------
type Obj = { [k: string]: unknown };
type Locale = 'ar' | 'en';
type Instant = Date | string | number;

const ARABIC_LETTER = /[؀-ۿ]/;

function obj(v: unknown): Obj {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {};
}
function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}
function contains(list: readonly unknown[], v: unknown): boolean {
  return list.indexOf(v) >= 0;
}
function has(v: unknown): boolean {
  return typeof v === 'string' && v.trim() !== '';
}
function own(o: Obj, k: string): boolean {
  return Object.prototype.hasOwnProperty.call(o, k);
}
/** Trimmed and cut to `max` (the cut never leaves trailing space, so normalising twice changes nothing). */
function str(v: unknown, max: number): string {
  if (typeof v !== 'string') return '';
  const t = v.trim();
  return t.length > max ? t.slice(0, max).replace(/\s+$/, '') : t;
}
function bool(v: unknown, dflt: boolean): boolean {
  return typeof v === 'boolean' ? v : dflt;
}
function numOrNull(v: unknown): number | null {
  if (typeof v === 'number') return isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(normalizeDigits(v.trim()));
    return isFinite(n) ? n : null;
  }
  return null;
}
function intOrNull(v: unknown, lo: number, hi: number): number | null {
  const n = numOrNull(v);
  if (n === null) return null;
  return Math.min(hi, Math.max(lo, Math.round(n)));
}
function toMs(t: Instant | null | undefined): number {
  if (t instanceof Date) return t.getTime();
  if (typeof t === 'number') return t;
  return typeof t === 'string' ? Date.parse(t) : NaN;
}
function round1(x: number): number {
  return Math.round(x * 10) / 10;
}
function pct(a: number, b: number): number {
  return b > 0 ? round1((a / b) * 100) : 0;
}
function isChoice(t: QuestionType): boolean {
  return contains(CHOICE_TYPES, t);
}
function hex8(h: number): string {
  return ('0000000' + (h >>> 0).toString(16)).slice(-8);
}

// ---- hashing -------------------------------------------------------------------------------------
/** FNV-1a 32-bit; the multiply by 16777619 is written with shifts (ES5). */
export function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24)) >>> 0;
  }
  return h >>> 0;
}

/** JSON with object keys sorted at every level (undefined members dropped), so equal content hashes equally. */
export function canonicalJson(v: unknown): string {
  if (v === null || typeof v !== 'object') return v === undefined ? 'null' : JSON.stringify(v);
  if (Array.isArray(v)) return '[' + v.map(canonicalJson).join(',') + ']';
  const o = v as Obj;
  const keys = Object.keys(o).sort();
  const parts: string[] = [];
  for (let i = 0; i < keys.length; i++) {
    if (o[keys[i]] === undefined) continue;
    parts.push(JSON.stringify(keys[i]) + ':' + canonicalJson(o[keys[i]]));
  }
  return '{' + parts.join(',') + '}';
}

/**
 * A22: `qid:type:required:optionId,…|` per question, in (page) order. Text is excluded, so wording edits keep it.
 * N.1.1 (TF1): with 2+ sections the partition is appended as `#` + the section sizes, so moving a question across a
 * section boundary is a new version; titles, descriptions and section ids never enter it. One section (every
 * migrated legacy variant) hashes exactly as before, so the deploy bumps no version.
 */
export function structureHash(variant: { questions: LeadFormQuestion[]; sections?: LeadFormSection[] }): string {
  let s = '';
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  for (let i = 0; i < qs.length; i++) {
    const q = qs[i];
    const ids = arr(q.options).map((o) => (o as ChoiceOption).id);
    s += q.id + ':' + q.type + ':' + (q.required ? '1' : '0') + ':' + ids.join(',') + '|';
  }
  const secs = arr(variant && variant.sections) as LeadFormSection[];
  if (secs.length > 1) s += '#' + secs.map((x) => arr(x && x.questionIds).length).join(',');
  return hex8(fnv1a(s));
}

export function contentHash(c: LeadFormContent): string {
  return hex8(fnv1a(canonicalJson(c)));
}

/** The same structure hash keeps the version; a new key or a different hash gives prev + 1 (or 1). */
export function nextVariantVersion(
  prevMeta: { [key: string]: { version: number; structureHash: string } } | null | undefined,
  key: string,
  hash: string,
): number {
  const prev = prevMeta && own(prevMeta as Obj, key) ? prevMeta[key] : null;
  if (!prev) return 1;
  return prev.structureHash === hash ? prev.version : prev.version + 1;
}

// ---- ids and variant assignment ------------------------------------------------------------------
const BASE36 = '0123456789abcdefghijklmnopqrstuvwxyz';

/** `prefix` + 8 base36 characters (matches QID_RE / OPTION_ID_RE / SECTION_ID_RE / CTA_ID_RE / FACT_ID_RE). */
export function newId(prefix: 'q' | 'o' | 's' | 'c' | 'f', rand: () => number): string {
  let s = prefix;
  for (let i = 0; i < 8; i++) {
    const r = rand();
    const k = Math.floor((r >= 0 && r < 1 ? r : 0) * 36);
    s += BASE36.charAt(k);
  }
  return s;
}

/** A stable bucket 0..9999 per (visitor, form). */
export function bucketOf(vid: string, formId: string): number {
  return fnv1a(String(formId) + ':' + String(vid)) % 10000;
}

/** Cumulative weights over active variants with weight > 0, in stored order, scaled to 10000. */
export function pickVariant(
  variants: { key: string; weight: number; active: boolean }[],
  bucket: number,
): string | null {
  const live = arr(variants).filter((v) => {
    const x = v as { weight: number; active: boolean };
    return !!x && x.active !== false && typeof x.weight === 'number' && x.weight > 0;
  }) as { key: string; weight: number }[];
  if (!live.length) return null;
  let total = 0;
  for (let i = 0; i < live.length; i++) total += live[i].weight;
  const b = Math.min(9999, Math.max(0, Math.floor(bucket)));
  let acc = 0;
  for (let i = 0; i < live.length; i++) {
    acc += live[i].weight;
    if (b * total < acc * 10000) return live[i].key;
  }
  return live[live.length - 1].key;
}

// ---- URLs ------------------------------------------------------------------------------------------
/** 'en' → '/en', 'ar' → ''. Never '/ar'. */
export function localePrefix(loc: Locale | string): string {
  return loc === 'en' ? '/en' : '';
}
/** '/apply/<slug>/' or '/apply/<slug>/<key>/' (locale-free, trailing slash). */
export function applyPath(slug: string, key: string | null): string {
  return '/apply/' + slug + '/' + (key ? key + '/' : '');
}
export function thanksPath(slug: string): string {
  return '/apply/' + slug + '/thanks/';
}
export function absoluteUrl(base: string, loc: Locale | string, path: string): string {
  return String(base || '').replace(/\/+$/, '') + localePrefix(loc) + path;
}
/** The full ad link; the {{…}} macros stay unencoded. */
export function adLink(base: string, loc: Locale | string, path: string): string {
  return absoluteUrl(base, loc, path) + '?' + META_AD_UTM;
}
/** A5: the per-diploma Custom Conversion rule rows, always against the prod host (never siteBase()). */
export function conversionRule(slug: string): string[] {
  return [PROD_SITE + '/apply/' + slug + '/thanks', PROD_SITE + '/en/apply/' + slug + '/thanks'];
}

// ---- general forms: type, URLs, identity, stash key (O.1.1, O.1.4) ---------------------------------------
/** O.1.1: a stored form without `type` (every form before 5 Oct) is a diploma form; 'general' only on an exact match. */
export function formTypeOf(doc: { type?: unknown } | null | undefined): FormType {
  return !!doc && doc.type === 'general' ? 'general' : 'diploma';
}
/** '/form/<slug>/' (locale-free, trailing slash): a namespace of its own, never under /apply/. */
export function generalPath(slug: string): string {
  return '/form/' + slug + '/';
}
export function generalThanksPath(slug: string): string {
  return '/form/' + slug + '/thanks/';
}
/** The per-form Custom Conversion rule rows of a general form (A5 shape, always the prod host). */
export function generalConversionRule(slug: string): string[] {
  return [PROD_SITE + '/form/' + slug + '/thanks', PROD_SITE + '/en/form/' + slug + '/thanks'];
}
/** content_name of a general form: the EN title, else the AR title, else the slug (never '', OQ4). */
export function generalContentName(h: GeneralHeader | null | undefined, slug: string): string {
  const o = obj(h);
  if (has(o.title)) return (o.title as string).trim();
  if (has(o.title_ar)) return (o.title_ar as string).trim();
  return String(slug || '');
}
/** The thanks-stash slug: a diploma keeps its slug; a general form is 'form:<slug>', which no diploma slug (and so
 *  no /apply/ thanks page) can ever equal (O.1.4). */
export function stashSlugFor(type: FormType | string, slug: string): string {
  return type === 'general' ? GENERAL_STASH_PREFIX + slug : slug;
}

// ---- sanitising, CSV, user agents, the thanks stash ------------------------------------------------
/** Arabic-Indic ٠-٩ and Persian ۰-۹ → 0-9. */
export function normalizeDigits(s: string): string {
  return String(s).replace(/[٠-٩۰-۹]/g, (d) => {
    const c = d.charCodeAt(0);
    return String(c >= 0x06f0 ? c - 0x06f0 : c - 0x0660);
  });
}

/** A12: trim, strip control characters, cut. Never rejects. */
export function sanitizeAttr(s: unknown, max: number): string {
  if (typeof s !== 'string') return '';
  const t = s.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  return t.length > max ? t.slice(0, max) : t;
}

export function sanitizeAttribution(a: unknown): LeadAttribution {
  const o = obj(a);
  const u = obj(o.utm);
  const touch = o.touch;
  return {
    utm: {
      source: sanitizeAttr(u.source, LIMITS.utm),
      medium: sanitizeAttr(u.medium, LIMITS.utm),
      campaign: sanitizeAttr(u.campaign, LIMITS.utm),
      content: sanitizeAttr(u.content, LIMITS.utm),
      term: sanitizeAttr(u.term, LIMITS.utm),
    },
    utmId: sanitizeAttr(o.utmId, LIMITS.utm),
    fbclid: sanitizeAttr(o.fbclid, LIMITS.clickId),
    gclid: sanitizeAttr(o.gclid, LIMITS.clickId),
    referrerHost: sanitizeAttr(o.referrerHost, LIMITS.referrerHost),
    landingPath: sanitizeAttr(o.landingPath, LIMITS.landingPath),
    touch: touch === 'url' || touch === 'session' || touch === 'stored' ? touch : 'direct',
  };
}

/**
 * One quoted CSV cell (a port of ADM leadsCsv.ts cell()). A value a spreadsheet could run as a formula (it starts
 * with = + - @ TAB or CR) gets a leading apostrophe; a value that is exactly an E.164 phone is left as it is.
 */
export function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  const risky = /^[=+\-@\t\r]/.test(s) && !E164_RE.test(s);
  return '"' + (risky ? "'" + s : s).replace(/"/g, '""') + '"';
}

/** B.3: an empty user agent is a bot too. */
export function isBotUa(ua: string | null | undefined): boolean {
  return typeof ua !== 'string' || ua.trim() === '' || BOT_UA_RE.test(ua);
}

/** F.4: the thanks stash belongs to this diploma and locale and is younger than stashTtlMs. */
export function stashValid(
  s: DoneStashLike | null | undefined,
  slug: string,
  locale: string,
  now: number,
): boolean {
  return !!s && s.v === 1 && s.slug === slug && s.locale === locale && typeof s.at === 'number' &&
    now - s.at < LIMITS.stashTtlMs;
}

// ---- defaults and templates (D.3, F.8, A30) -------------------------------------------------------
export const DEFAULT_CONSENT_EN =
  'I agree that Kayfa may contact me about this diploma and use my answers to improve its programmes and ads.';
export const DEFAULT_CONSENT_AR =
  'أوافق على أن تتواصل معي أكاديمية كيف بخصوص هذا الدبلوم وأن تستخدم إجاباتي لتحسين برامجها وإعلاناتها.';
export const DEFAULT_MARKETING_EN = 'Send me news about new diplomas and offers.';
export const DEFAULT_MARKETING_AR = 'أرسلوا لي أخبار الدبلومات الجديدة والعروض.';
export const DEFAULT_THANKS = {
  title: 'Thank you! Your application is in',
  title_ar: 'شكرًا لك! وصلنا طلبك',
  body: 'Our team will contact you soon by WhatsApp or phone to help you with the next step.',
  body_ar: 'سيتواصل معك فريقنا قريبًا عبر واتساب أو الهاتف ليساعدك في الخطوة التالية.',
};
// O.1.3: the general form's copy (no diploma named). Swapped in by toGeneralContent on exact matches only.
export const DEFAULT_CONSENT_GENERAL_EN =
  'I agree that Kayfa may contact me about my request and use my answers to improve its programmes and ads.';
export const DEFAULT_CONSENT_GENERAL_AR =
  'أوافق على أن تتواصل معي أكاديمية كيف بخصوص طلبي وأن تستخدم إجاباتي لتحسين برامجها وإعلاناتها.';
export const DEFAULT_THANKS_GENERAL = {
  title: "Thank you! We've got your request",
  title_ar: 'شكرًا لك! وصلنا طلبك',
  body: DEFAULT_THANKS.body,
  body_ar: DEFAULT_THANKS.body_ar,
};

/** O.1.2: no cover (the compact header), no title, no facts. */
export function emptyGeneralHeader(): GeneralHeader {
  return { cover: '', title: '', title_ar: '', subtitle: '', subtitle_ar: '', facts: [] };
}

/** N.1.1: a section with no heading and no questions (the ADM adds questions to it). */
export function emptySection(id: string): LeadFormSection {
  return { id, title: '', title_ar: '', description: '', description_ar: '', questionIds: [] };
}

/** N.1.3: one thank-you button of `type`, every text '' (→ the i18n default of the type). */
export function emptyCta(type: ThanksCtaType, id: string, enabled?: boolean): ThanksCta {
  return { id, type, enabled: enabled !== false, label: '', label_ar: '', value: '', prefill: '', prefill_ar: '' };
}

/** N.1.3 defaults and the migration of a legacy `thanks {whatsapp, diplomaLink}`: WhatsApp, diploma page, call (off). */
export function defaultCtas(whatsapp?: boolean, diplomaLink?: boolean): ThanksCta[] {
  return [
    emptyCta('whatsapp', 'cwhatsapp', whatsapp !== false),
    emptyCta('diploma_page', 'cdiploma', diplomaLink !== false),
    emptyCta('call', 'ccallus', false),
  ];
}

export function emptyCommon(): LeadFormCommon {
  return {
    consent: {
      mode: 'required',
      text: DEFAULT_CONSENT_EN,
      text_ar: DEFAULT_CONSENT_AR,
      marketing: { show: true, text: DEFAULT_MARKETING_EN, text_ar: DEFAULT_MARKETING_AR },
    },
    thanks: {
      title: DEFAULT_THANKS.title,
      title_ar: DEFAULT_THANKS.title_ar,
      body: DEFAULT_THANKS.body,
      body_ar: DEFAULT_THANKS.body_ar,
      ctas: defaultCtas(true, true),
    },
    closed: { title: '', title_ar: '', body: '', body_ar: '' },
    submitLabel: '',
    submitLabel_ar: '',
    header: { facts: HEADER_FACT_KEYS.slice() },
  };
}

/** Variant `a` is the control at 100; a later key starts at 50 (the admin sets the split). One untitled section. */
export function emptyVariant(key: string): LeadFormVariant {
  return {
    key,
    name: key === 'a' ? 'Control' : 'Variant ' + String(key).toUpperCase(),
    weight: key === 'a' ? 100 : 50,
    active: true,
    sections: [emptySection(DEFAULT_SECTION_ID)],
    questions: [],
  };
}

export function emptyContent(): LeadFormContent {
  return { common: emptyCommon(), variants: [emptyVariant('a')] };
}

export function emptyQuestion(type: QuestionType, id: string, optionIds?: string[]): LeadFormQuestion {
  return {
    id,
    type,
    required: type !== 'email', // P.1.6: a new email question starts optional (phone first; email is secondary)
    label: '',
    label_ar: '',
    help: '',
    help_ar: '',
    placeholder: '',
    placeholder_ar: '',
    semantic: '',
    options: isChoice(type) ? arr(optionIds).map((oid) => ({ id: String(oid), label: '', label_ar: '' })) : [],
    minSelect: null,
    maxSelect: null,
    maxLength: null,
    min: null,
    max: null,
    integer: false,
    minDate: '',
    maxDate: '',
    defaultCountry: type === 'phone' ? DEFAULT_PHONE_COUNTRY : '',
  };
}

type IdGen = (prefix: 'q' | 'o') => string;
type Pair = [string, string];

function tq(idGen: IdGen, type: QuestionType, label: Pair, more?: Partial<LeadFormQuestion>, options?: Pair[]) {
  const q = emptyQuestion(type, idGen('q'));
  q.label = label[0];
  q.label_ar = label[1];
  if (options) q.options = options.map((o) => ({ id: idGen('o'), label: o[0], label_ar: o[1] }));
  const m = (more || {}) as Obj;
  const keys = Object.keys(m);
  for (let i = 0; i < keys.length; i++) (q as unknown as Obj)[keys[i]] = m[keys[i]];
  return q;
}

/** A titled template section over `qs` (fixed ids: section ids are unique per variant only, N.1.1). */
function ts(id: string, title: Pair, description: Pair, qs: LeadFormQuestion[]): LeadFormSection {
  return {
    id,
    title: title[0],
    title_ar: title[1],
    description: description[0],
    description_ar: description[1],
    questionIds: qs.map((q) => q.id),
  };
}

/**
 * D.3 / N.2.2 templates: fully bilingual, positive-tone copy, publishable as they are, with the default thank-you
 * buttons and header facts. Quick = 2 sections, Qualify = 3 sections, Blank = one untitled section.
 */
export function formTemplate(kind: 'quick' | 'qualify' | 'blank', idGen: IdGen): LeadFormContent {
  const c = emptyContent();
  const v = c.variants[0];
  const name = () =>
    tq(idGen, 'short_text', ["What's your full name?", 'ما اسمك بالكامل؟'], {
      semantic: 'name',
      placeholder: 'Your full name',
      placeholder_ar: 'اسمك بالكامل',
    });
  if (kind === 'blank') {
    v.questions = [name()];
    v.sections[0].questionIds = [v.questions[0].id];
    return c;
  }
  const you = name();
  const phone = tq(idGen, 'phone', ['Your WhatsApp number', 'رقم الواتساب الخاص بك'], {
    help: "We'll send you the diploma details there.",
    help_ar: 'سنرسل لك تفاصيل الدبلوم عليه.',
  });
  const level = tq(idGen, 'single_choice', ["What's your current level?", 'ما مستواك الحالي؟'], {}, [
    ['Just starting out', 'أبدأ من الصفر'],
    ['I know the basics', 'أعرف الأساسيات'],
    ['I already work in the field', 'أعمل بالفعل في المجال'],
  ]);
  const start = tq(idGen, 'single_choice', ['When would you like to start?', 'متى تحب أن تبدأ؟'], {}, [
    ['This month', 'هذا الشهر'],
    ['Next month', 'الشهر القادم'],
    ['A little later', 'بعد ذلك بقليل'],
  ]);
  const aboutYou: Pair = ['About you', 'عنك'];
  const aboutYouLine: Pair = ['So we can reach you', 'لنتمكّن من التواصل معك'];
  if (kind === 'quick') {
    v.questions = [you, phone, level, start];
    v.sections = [
      ts('saboutyou', aboutYou, aboutYouLine, [you, phone]),
      ts('syourplans', ['Your plans', 'خططك'],
        ['Helps us suggest the right start for you', 'تساعدنا على اقتراح البداية المناسبة لك'], [level, start]),
    ];
    return c;
  }
  const describe = tq(idGen, 'single_choice', ['What best describes you?', 'ما الذي يصفك أكثر؟'], {}, [
    ['Student', 'طالب'],
    ['Employee', 'موظف'],
    ['Freelancer', 'أعمل بشكل حر'],
    ['Looking for work', 'أبحث عن عمل'],
  ]);
  const goal = tq(idGen, 'single_choice', ["What's your main goal?", 'ما هدفك الأساسي؟'], {}, [
    ['Land a new job', 'الحصول على وظيفة جديدة'],
    ['Grow in my current job', 'التطور في عملي الحالي'],
    ['Start freelancing', 'بدء العمل الحر'],
    ['Learn a new skill', 'تعلّم مهارة جديدة'],
  ]);
  const email = tq(idGen, 'email', ['Your email', 'بريدك الإلكتروني'], { required: false });
  const callTime = tq(idGen, 'dropdown', ["What's the best time to call you?", 'ما أنسب وقت للاتصال بك؟'], {}, [
    ['Morning (9 am to 12 pm)', 'صباحًا (من 9 إلى 12)'],
    ['Afternoon (12 to 5 pm)', 'بعد الظهر (من 12 إلى 5)'],
    ['Evening (5 to 9 pm)', 'مساءً (من 5 إلى 9)'],
  ]);
  v.questions = [you, phone, email, describe, level, goal, start, callTime];
  v.sections = [
    ts('saboutyou', aboutYou, aboutYouLine, [you, phone, email]),
    ts('swhereyouare', ['Where you are now', 'وضعك الحالي'],
      ['So we suggest what fits your level', 'لنقترح عليك ما يناسب مستواك'], [describe, level]),
    ts('syourgoal', ['Your goal', 'هدفك'],
      ['So we can help you get there', 'لنساعدك على الوصول إليه'], [goal, start, callTime]),
  ];
  return c;
}

// ---- normalisation (B.7: picks known keys, coerces, trims, cuts, clears fields that do not fit) --------
// Lists are cut only at a safety bound (twice the limit) so validateContentShape can still report "at most N".
function normalizeOption(raw: unknown): ChoiceOption {
  const o = obj(raw);
  return {
    id: typeof o.id === 'string' ? o.id.trim() : '',
    label: str(o.label, LIMITS.option),
    label_ar: str(o.label_ar, LIMITS.option),
  };
}

function normalizeDate(v: unknown): string {
  const s = typeof v === 'string' ? normalizeDigits(v.trim()) : '';
  return DATE_RE.test(s) ? s : '';
}

export function normalizeQuestion(raw: unknown): LeadFormQuestion {
  const o = obj(raw);
  const type: QuestionType = contains(QUESTION_TYPES, o.type) ? (o.type as QuestionType) : 'short_text';
  const text = type === 'short_text' || type === 'long_text';
  const withPlaceholder = text || type === 'email' || type === 'number';
  const textCap = type === 'long_text' ? LIMITS.longTextCap : LIMITS.shortTextCap;
  const country = typeof o.defaultCountry === 'string' ? o.defaultCountry.trim().toUpperCase() : '';
  const q: LeadFormQuestion = {
    id: typeof o.id === 'string' ? o.id.trim() : '',
    type,
    required: bool(o.required, true),
    label: str(o.label, LIMITS.label),
    label_ar: str(o.label_ar, LIMITS.label),
    help: str(o.help, LIMITS.help),
    help_ar: str(o.help_ar, LIMITS.help),
    placeholder: withPlaceholder ? str(o.placeholder, LIMITS.placeholder) : '',
    placeholder_ar: withPlaceholder ? str(o.placeholder_ar, LIMITS.placeholder) : '',
    semantic: type === 'short_text' && o.semantic === 'name' ? 'name' : '',
    options: isChoice(type) ? arr(o.options).slice(0, LIMITS.dropdownOptions * 2).map(normalizeOption) : [],
    minSelect: type === 'multi_choice' ? intOrNull(o.minSelect, 0, LIMITS.dropdownOptions) : null,
    maxSelect: type === 'multi_choice' ? intOrNull(o.maxSelect, 0, LIMITS.dropdownOptions) : null,
    maxLength: text ? intOrNull(o.maxLength, 1, textCap) : null,
    min: type === 'number' ? numOrNull(o.min) : null,
    max: type === 'number' ? numOrNull(o.max) : null,
    integer: type === 'number' ? bool(o.integer, false) : false,
    minDate: type === 'date' ? normalizeDate(o.minDate) : '',
    maxDate: type === 'date' ? normalizeDate(o.maxDate) : '',
    defaultCountry: type === 'phone' ? (/^[A-Z]{2}$/.test(country) ? country : DEFAULT_PHONE_COUNTRY) : '',
  };
  // P.1.4 presence rule: the key exists only for 'any' on a phone question (absent = mobile), so every stored draft
  // and revision normalises and hashes exactly as before P.
  if (type === 'phone' && o.phoneMode === 'any') q.phoneMode = 'any';
  return q;
}

/** A deterministic replacement id (`sauto00`, `cauto01`, …): the list index, bumped past ids already taken; the id
 * returned is marked taken in `used`. */
function autoId(head: string, i: number, used: Obj): string {
  let k = i;
  let id = '';
  do {
    id = head + (k < 10 ? '0' + k : String(k));
    k++;
  } while (used['~' + id]);
  used['~' + id] = 1;
  return id;
}

/**
 * Two passes, so a valid stored id is never renamed: every valid id keeps its name at its first occurrence (all
 * reserved in `used` first), and only the invalid or repeated ones (`bad[i]`) then get an auto id that skips them.
 */
function reserveIds(list: { id: string }[], re: RegExp): { used: Obj; bad: boolean[] } {
  const used: Obj = {};
  const bad: boolean[] = [];
  for (let i = 0; i < list.length; i++) {
    const ok = re.test(list[i].id) && !used['~' + list[i].id];
    if (ok) used['~' + list[i].id] = 1;
    bad.push(!ok);
  }
  return { used, bad };
}

function normalizeSection(raw: unknown): LeadFormSection {
  const o = obj(raw);
  const ids = arr(o.questionIds).slice(0, LIMITS.questions * 2);
  const questionIds: string[] = [];
  for (let i = 0; i < ids.length; i++) if (typeof ids[i] === 'string') questionIds.push((ids[i] as string).trim());
  return {
    id: typeof o.id === 'string' ? o.id.trim() : '',
    title: str(o.title, LIMITS.sectionTitle),
    title_ar: str(o.title_ar, LIMITS.sectionTitle),
    description: str(o.description, LIMITS.sectionDescription),
    description_ar: str(o.description_ar, LIMITS.sectionDescription),
    questionIds,
  };
}

/**
 * N.1.1 canonical order, steps 3-6, on a variant whose questions are already normalised (the ADM calls it after
 * every section or question move): section strings cut; a bad or repeated section id becomes `sauto` + index (a
 * valid id keeps its name at its first occurrence, so an earlier bad id never takes it);
 * `questionIds` keep only qids of `questions`, first occurrence wins; qids in no section go, in `questions` order, to
 * the LAST section (a new DEFAULT_SECTION_ID one if there is none); `questions` is re-ordered to the concatenation of
 * the sections' questionIds; empty sections are kept; sections beyond twice the limit are cut. Returns a new
 * variant (other own keys, e.g. a public `version`, are kept); never mutates its argument.
 */
export function applySectionOrder<V extends LeadFormVariant>(variant: V): V {
  const src = obj(variant);
  const qs = arr(src.questions) as LeadFormQuestion[];
  const raw = arr(src.sections).slice(0, LIMITS.sections * 2);
  const exists: Obj = {};
  for (let i = 0; i < qs.length; i++) exists['~' + qs[i].id] = 1;
  const placed: Obj = {};
  const sections: LeadFormSection[] = [];
  for (let i = 0; i < raw.length; i++) sections.push(normalizeSection(raw[i]));
  const fresh = reserveIds(sections, SECTION_ID_RE);
  for (let i = 0; i < sections.length; i++) {
    const sec = sections[i];
    if (fresh.bad[i]) sec.id = autoId('sauto', i, fresh.used);
    const kept: string[] = [];
    for (let j = 0; j < sec.questionIds.length; j++) {
      const qid = sec.questionIds[j];
      if (exists['~' + qid] && !placed['~' + qid]) {
        placed['~' + qid] = 1;
        kept.push(qid);
      }
    }
    sec.questionIds = kept;
  }
  const orphans: string[] = [];
  for (let i = 0; i < qs.length; i++) {
    if (!placed['~' + qs[i].id]) {
      placed['~' + qs[i].id] = 1;
      orphans.push(qs[i].id);
    }
  }
  if (orphans.length) {
    if (!sections.length) sections.push(emptySection(DEFAULT_SECTION_ID));
    const last = sections[sections.length - 1];
    last.questionIds = last.questionIds.concat(orphans);
  }
  // Questions that share a qid (a shape error reported elsewhere) stay together, so none is ever lost.
  const byId: { [k: string]: LeadFormQuestion[] } = {};
  for (let i = 0; i < qs.length; i++) (byId['~' + qs[i].id] = byId['~' + qs[i].id] || []).push(qs[i]);
  const ordered: LeadFormQuestion[] = [];
  for (let i = 0; i < sections.length; i++) {
    const ids = sections[i].questionIds;
    for (let j = 0; j < ids.length; j++) {
      const group = byId['~' + ids[j]] || [];
      for (let g = 0; g < group.length; g++) ordered.push(group[g]);
    }
  }
  const out: Obj = {};
  const keys = Object.keys(src);
  for (let i = 0; i < keys.length; i++) out[keys[i]] = src[keys[i]];
  out.sections = sections;
  out.questions = ordered;
  return out as unknown as V;
}

/** A stored `intro` (before 1 Oct) is ignored; a variant without `sections` becomes one untitled section (N.1.1). */
export function normalizeVariant(raw: unknown): LeadFormVariant {
  const o = obj(raw);
  const w = numOrNull(o.weight);
  return applySectionOrder({
    key: typeof o.key === 'string' ? o.key.trim() : '',
    name: str(o.name, LIMITS.name),
    weight: w === null ? 100 : Math.min(100, Math.max(0, Math.round(w))),
    active: bool(o.active, true),
    sections: Array.isArray(o.sections) ? (o.sections as LeadFormSection[]) : [emptySection(DEFAULT_SECTION_ID)],
    questions: arr(o.questions).slice(0, LIMITS.questions * 2).map(normalizeQuestion),
  });
}

function normalizeCta(raw: unknown): ThanksCta | null {
  const o = obj(raw);
  if (!contains(THANKS_CTA_TYPES, o.type)) return null;
  const type = o.type as ThanksCtaType;
  const phoneLike = type === 'whatsapp' || type === 'call';
  return {
    id: typeof o.id === 'string' ? o.id.trim() : '',
    type,
    enabled: bool(o.enabled, true),
    label: str(o.label, LIMITS.ctaLabel),
    label_ar: str(o.label_ar, LIMITS.ctaLabel),
    // custom: kept up to twice the limit, never cut to it, so ctaProblems reports an over-long URL as invalid_url
    value: type === 'custom' ? str(o.value, LIMITS.ctaUrl * 2) : phoneLike ? cleanPhone(str(o.value, 40)) : '',
    prefill: type === 'whatsapp' ? str(o.prefill, LIMITS.ctaPrefill) : '',
    prefill_ar: type === 'whatsapp' ? str(o.prefill_ar, LIMITS.ctaPrefill) : '',
  };
}

/** N.1.3: `thanks.ctas` when it is an array (unknown types dropped, bad/repeated ids → `cauto` + index), else the
 * migration of the legacy `whatsapp` / `diplomaLink` booleans (both default on) plus the call button (off). */
function normalizeCtas(thanks: Obj): ThanksCta[] {
  if (!Array.isArray(thanks.ctas)) return defaultCtas(bool(thanks.whatsapp, true), bool(thanks.diplomaLink, true));
  const list = (thanks.ctas as unknown[]).slice(0, LIMITS.ctas * 2);
  const out: ThanksCta[] = [];
  for (let i = 0; i < list.length; i++) {
    const cta = normalizeCta(list[i]);
    if (cta) out.push(cta);
  }
  const fresh = reserveIds(out, CTA_ID_RE);
  for (let i = 0; i < out.length; i++) if (fresh.bad[i]) out[i].id = autoId('cauto', i, fresh.used);
  return out;
}

/** N.1.4: known keys, no repeats, in the stored order; a missing list is all 8 in the default order. */
function normalizeHeader(raw: unknown): { facts: HeaderFactKey[] } {
  const o = obj(raw);
  if (!Array.isArray(o.facts)) return { facts: HEADER_FACT_KEYS.slice() };
  const facts: HeaderFactKey[] = [];
  const list = o.facts as unknown[];
  for (let i = 0; i < list.length && facts.length < HEADER_FACT_KEYS.length; i++) {
    if (contains(HEADER_FACT_KEYS, list[i]) && !contains(facts, list[i])) facts.push(list[i] as HeaderFactKey);
  }
  return { facts };
}

const COVER_URL_RE = /^https?:\/\/[^\s"'<>]+$/;

function normalizeGeneralFact(raw: unknown): GeneralFact {
  const o = obj(raw);
  return {
    id: typeof o.id === 'string' ? o.id.trim() : '',
    icon: contains(GENERAL_FACT_ICONS, o.icon) ? (o.icon as GeneralFactIcon) : 'check',
    label: str(o.label, LIMITS.factLabel),
    label_ar: str(o.label_ar, LIMITS.factLabel),
    value: str(o.value, LIMITS.factValue),
    value_ar: str(o.value_ar, LIMITS.factValue),
  };
}

/**
 * O.1.2, never throws: the cover is kept only as an absolute http(s) URL (else '' = no cover; http for the local
 * MinIO); texts cut to the LIMITS; facts cut at twice the limit (validateContentShape reports "at most 6"), an unknown
 * icon becomes 'check', a fact whose four texts are all '' is dropped, a bad or repeated id becomes `fauto` + index.
 */
export function normalizeGeneralHeader(raw: unknown): GeneralHeader {
  const o = obj(raw);
  const cover = str(o.cover, LIMITS.coverUrl);
  const list = arr(o.facts).slice(0, LIMITS.generalFacts * 2);
  const facts: GeneralFact[] = [];
  for (let i = 0; i < list.length; i++) {
    const f = normalizeGeneralFact(list[i]);
    if (f.label || f.label_ar || f.value || f.value_ar) facts.push(f);
  }
  const fresh = reserveIds(facts, FACT_ID_RE);
  for (let i = 0; i < facts.length; i++) if (fresh.bad[i]) facts[i].id = autoId('fauto', i, fresh.used);
  return {
    cover: COVER_URL_RE.test(cover) ? cover : '',
    title: str(o.title, LIMITS.generalTitle),
    title_ar: str(o.title_ar, LIMITS.generalTitle),
    subtitle: str(o.subtitle, LIMITS.generalSubtitle),
    subtitle_ar: str(o.subtitle_ar, LIMITS.generalSubtitle),
    facts,
  };
}

function isObj(v: unknown): boolean {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function normalizeCommon(raw: unknown): LeadFormCommon {
  const o = obj(raw);
  const consent = obj(o.consent);
  const marketing = obj(consent.marketing);
  const thanks = obj(o.thanks);
  const closed = obj(o.closed);
  const common: LeadFormCommon = {
    consent: {
      mode: contains(CONSENT_MODES, consent.mode) ? (consent.mode as ConsentMode) : 'required',
      text: str(consent.text, LIMITS.consent),
      text_ar: str(consent.text_ar, LIMITS.consent),
      marketing: {
        show: bool(marketing.show, false),
        text: str(marketing.text, LIMITS.consent),
        text_ar: str(marketing.text_ar, LIMITS.consent),
      },
    },
    thanks: {
      title: str(thanks.title, LIMITS.thanksTitle),
      title_ar: str(thanks.title_ar, LIMITS.thanksTitle),
      body: str(thanks.body, LIMITS.thanksBody),
      body_ar: str(thanks.body_ar, LIMITS.thanksBody),
      ctas: normalizeCtas(thanks),
    },
    closed: {
      title: str(closed.title, LIMITS.thanksTitle),
      title_ar: str(closed.title_ar, LIMITS.thanksTitle),
      body: str(closed.body, LIMITS.closedBody),
      body_ar: str(closed.body_ar, LIMITS.closedBody),
    },
    submitLabel: str(o.submitLabel, LIMITS.cta),
    submitLabel_ar: str(o.submitLabel_ar, LIMITS.cta),
    header: normalizeHeader(o.header),
  };
  // O.1.2 presence rule: the key exists only when the input has a header object, so no diploma document (none has
  // the key) changes its normalised content or its hashes.
  if (isObj(o.generalHeader)) common.generalHeader = normalizeGeneralHeader(o.generalHeader);
  if (isObj(o.almost)) common.almost = normalizeAlmost(o.almost);   // R.1.2 presence rule (SPEC-R)
  return common;
}

/** Never throws. Unknown keys are dropped; a missing string is '', a missing boolean takes its field default
 * (required/active/CTA enabled on; marketing opt-in and integer off). Legacy content (an `intro`, no `sections`,
 * `thanks.whatsapp/diplomaLink`, no `header`) migrates on read (N.1.1-N.1.4); Mongo is never rewritten. */
export function normalizeContent(raw: unknown): LeadFormContent {
  const o = obj(raw);
  return {
    common: normalizeCommon(o.common),
    variants: arr(o.variants).slice(0, LIMITS.variants * 2).map(normalizeVariant),
  };
}

/**
 * O.1.3, the normalisation by form type (save, publish, payloads). 'diploma': normalizeContent with any
 * `generalHeader` removed (a crafted draft never gives a diploma form a header; every stored diploma draft comes out
 * exactly as normalizeContent). 'general': normalizeContent, the empty header when absent, no `diploma_page` button.
 */
export function normalizeContentFor(raw: unknown, type: FormType | string): LeadFormContent {
  const c = normalizeContent(raw);
  if (type !== 'general') {
    if (own(c.common as unknown as Obj, 'generalHeader')) delete c.common.generalHeader;
    return c;
  }
  if (!c.common.generalHeader) c.common.generalHeader = emptyGeneralHeader();
  c.common.thanks.ctas = c.common.thanks.ctas.filter((x) => x.type !== 'diploma_page');
  return c;
}

/**
 * O.1.3, create and duplicate of a general form only (never on save): normalizeContentFor(c, 'general') plus the
 * exact-match copy swaps (the diploma consent and thank-you defaults → the general ones; a builder's own text is
 * never touched) and the header facts → ['time'] when it had 'time', else [] (diploma fact keys need a diploma).
 */
export function toGeneralContent(c: LeadFormContent): LeadFormContent {
  const g = normalizeContentFor(c, 'general');
  const consent = g.common.consent;
  if (consent.text === DEFAULT_CONSENT_EN) consent.text = DEFAULT_CONSENT_GENERAL_EN;
  if (consent.text_ar === DEFAULT_CONSENT_AR) consent.text_ar = DEFAULT_CONSENT_GENERAL_AR;
  const thanks = g.common.thanks;
  if (thanks.title === DEFAULT_THANKS.title) thanks.title = DEFAULT_THANKS_GENERAL.title;
  if (thanks.title_ar === DEFAULT_THANKS.title_ar) thanks.title_ar = DEFAULT_THANKS_GENERAL.title_ar;
  if (thanks.body === DEFAULT_THANKS.body) thanks.body = DEFAULT_THANKS_GENERAL.body;
  if (thanks.body_ar === DEFAULT_THANKS.body_ar) thanks.body_ar = DEFAULT_THANKS_GENERAL.body_ar;
  g.common.header = { facts: contains(g.common.header.facts, 'time') ? ['time'] : [] };
  return g;
}

/** O.1.3: 'diploma' = formTemplate (unchanged); 'general' = the same sections and questions as a general form. */
export function formTemplateFor(type: FormType | string, kind: 'quick' | 'qualify' | 'blank', idGen: IdGen): LeadFormContent {
  const c = formTemplate(kind, idGen);
  return type === 'general' ? toGeneralContent(c) : c;
}

/** Structural errors a draft may not be saved with, as `path: message` (the ADM mapFieldErrors format). */
export function validateContentShape(c: LeadFormContent): string[] {
  const errors: string[] = [];
  const variants = arr(c && c.variants) as LeadFormVariant[];
  if (!variants.length) errors.push('variants: at least 1');
  if (variants.length > LIMITS.variants) errors.push('variants: at most ' + LIMITS.variants);
  const seenKeys: Obj = {};
  const typeOf: { [qid: string]: { type: string; key: string } } = {};
  for (let i = 0; i < variants.length; i++) {
    const v = variants[i];
    const vp = 'variants.' + i;
    if (!VARIANT_KEY_RE.test(v.key)) errors.push(vp + '.key: invalid key');
    else if (seenKeys[v.key]) errors.push(vp + '.key: duplicate key');
    seenKeys[v.key] = 1;
    const qs = arr(v.questions) as LeadFormQuestion[];
    if (qs.length > LIMITS.questions) errors.push(vp + '.questions: at most ' + LIMITS.questions);
    const seenQ: Obj = {};
    for (let j = 0; j < qs.length; j++) {
      const q = qs[j];
      const qp = vp + '.questions.' + j;
      if (!QID_RE.test(q.id)) errors.push(qp + '.id: invalid id');
      else if (seenQ[q.id]) errors.push(qp + '.id: duplicate id');
      else {
        const prev = own(typeOf, q.id) ? typeOf[q.id] : null;
        if (prev && prev.type !== q.type) errors.push(qp + '.type: differs from variant ' + prev.key);
        if (!prev) typeOf[q.id] = { type: q.type, key: v.key };
      }
      seenQ[q.id] = 1;
      const opts = arr(q.options) as ChoiceOption[];
      const max = q.type === 'dropdown' ? LIMITS.dropdownOptions : LIMITS.options;
      if (opts.length > max) errors.push(qp + '.options: at most ' + max);
      const seenO: Obj = {};
      for (let k = 0; k < opts.length; k++) {
        if (!OPTION_ID_RE.test(opts[k].id)) errors.push(qp + '.options.' + k + '.id: invalid id');
        else if (seenO[opts[k].id]) errors.push(qp + '.options.' + k + '.id: duplicate id');
        seenO[opts[k].id] = 1;
      }
    }
    const secs = arr(v.sections) as LeadFormSection[];
    if (secs.length > LIMITS.sections) errors.push(vp + '.sections: at most ' + LIMITS.sections);
    const seenS: Obj = {};
    for (let k = 0; k < secs.length; k++) {
      const id = secs[k] && secs[k].id;
      if (typeof id !== 'string' || !SECTION_ID_RE.test(id)) errors.push(vp + '.sections.' + k + '.id: invalid id');
      else if (seenS['~' + id]) errors.push(vp + '.sections.' + k + '.id: duplicate id');
      seenS['~' + id] = 1;
    }
  }
  const ctas = arr(obj(obj(c && c.common).thanks).ctas) as ThanksCta[];
  if (ctas.length > LIMITS.ctas) errors.push('common.thanks.ctas: at most ' + LIMITS.ctas);
  const seenC: Obj = {};
  const typeCount: { [t: string]: number } = {};
  for (let k = 0; k < ctas.length; k++) {
    const cp = 'common.thanks.ctas.' + k;
    const cta = ctas[k] || ({} as ThanksCta);
    if (!CTA_ID_RE.test(String(cta.id))) errors.push(cp + '.id: invalid id');
    else if (seenC['~' + cta.id]) errors.push(cp + '.id: duplicate id');
    seenC['~' + cta.id] = 1;
    const n = (typeCount['~' + cta.type] = (typeCount['~' + cta.type] || 0) + 1);
    if (!contains(THANKS_CTA_TYPES, cta.type)) errors.push(cp + '.type: invalid type');
    else if (cta.type === 'custom' && n > LIMITS.customCtas) {
      errors.push(cp + '.type: at most ' + LIMITS.customCtas + ' custom links');
    } else if (cta.type !== 'custom' && n > 1) errors.push(cp + '.type: duplicate type');
  }
  // O.1.2: only when the content has a general header (never on a diploma form).
  const gh = obj(c && c.common).generalHeader;
  if (isObj(gh)) {
    const facts = arr((gh as Obj).facts) as GeneralFact[];
    if (facts.length > LIMITS.generalFacts) errors.push('common.generalHeader.facts: at most ' + LIMITS.generalFacts);
    const seenF: Obj = {};
    for (let k = 0; k < facts.length; k++) {
      const id = facts[k] && facts[k].id;
      const fp = 'common.generalHeader.facts.' + k;
      if (typeof id !== 'string' || !FACT_ID_RE.test(id)) errors.push(fp + '.id: invalid id');
      else if (seenF['~' + id]) errors.push(fp + '.id: duplicate id');
      seenF['~' + id] = 1;
    }
  }
  return errors;
}

// ---- sections, thank-you buttons and header facts (N.1.1, N.1.3, N.1.4) --------------------------------
/** The id of the section holding `qid` in this variant, '' when none does. */
export function sectionOf(variant: { sections?: LeadFormSection[] } | null | undefined, qid: string): string {
  const secs = arr(variant && variant.sections) as LeadFormSection[];
  for (let i = 0; i < secs.length; i++) if (contains(arr(secs[i].questionIds), qid)) return secs[i].id;
  return '';
}

/** True when a section shows a heading (and a marker) to visitors. */
export function isTitledSection(s: Pick<LeadFormSection, 'title' | 'title_ar'> | null | undefined): boolean {
  return !!s && (has(s.title) || has(s.title_ar));
}

const HTTPS_URL_RE = /^https:\/\/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{2,59})(?::\d{1,5})?(?:[/?#]\S*)?$/i;

/** An absolute https:// URL with a dotted host (no credentials, no spaces; punycode TLDs allowed), at most
 * LIMITS.ctaUrl long. */
export function isHttpsUrl(s: unknown): boolean {
  return typeof s === 'string' && s.length <= LIMITS.ctaUrl && HTTPS_URL_RE.test(s);
}

/**
 * The problems of one thank-you button, in field order ([] = fine). custom: label AR+EN and an https:// URL; call:
 * an E.164 number; whatsapp: '' (Kayfa's number) or E.164; label and WhatsApp message BOTH or NEITHER. Readiness
 * checks only enabled buttons (`ctas`); the ADM editor shows the same list inline.
 */
export function ctaProblems(cta: ThanksCta): CtaProblem[] {
  const c = obj(cta);
  const type = c.type;
  if (!contains(THANKS_CTA_TYPES, type)) return [{ field: 'type', reason: 'not_allowed' }];
  const out: CtaProblem[] = [];
  const both = (field: 'label' | 'prefill') => {
    const en = has(c[field]);
    const ar = has(c[field + '_ar']);
    if (en && !ar) out.push({ field: (field + '_ar') as CtaProblemField, reason: 'one_language' });
    else if (ar && !en) out.push({ field, reason: 'one_language' });
  };
  if (type === 'custom') {
    if (!has(c.label)) out.push({ field: 'label', reason: 'required' });
    if (!has(c.label_ar)) out.push({ field: 'label_ar', reason: 'required' });
  } else {
    both('label');
  }
  const value = typeof c.value === 'string' ? c.value.trim() : '';
  if (type === 'custom') {
    if (!value) out.push({ field: 'value', reason: 'required' });
    else if (!isHttpsUrl(value)) out.push({ field: 'value', reason: 'invalid_url' });
  } else if (type === 'call') {
    if (!value) out.push({ field: 'value', reason: 'required' });
    else if (!E164_RE.test(cleanPhone(value))) out.push({ field: 'value', reason: 'invalid_phone' });
  } else if (type === 'whatsapp') {
    if (value && !E164_RE.test(cleanPhone(value))) out.push({ field: 'value', reason: 'invalid_phone' });
    both('prefill');
  } else if (value) {
    out.push({ field: 'value', reason: 'not_allowed' });
  }
  return out;
}

function positive(v: unknown): boolean {
  return typeof v === 'number' && isFinite(v) && v > 0;
}

/**
 * Whether a header fact has data to show (N.1.4: a fact without data is never rendered, no placeholder). `time` is
 * the variant's estimate, not diploma data, so it is always false here (it renders as the time line, N.3.3).
 */
export function factHasData(facts: ApplyFacts | null | undefined, key: HeaderFactKey | string): boolean {
  const f = obj(facts);
  if (key === 'start') {
    const st = obj(f.start);
    if (!(typeof st.date === 'string' && DATE_RE.test(st.date))) return false;
    if (st.state === 'late_join') return has(st.closesAt);
    return st.state === 'open' || st.state === 'starting_soon';
  }
  if (key === 'duration') return positive(f.weeks);
  if (key === 'hours') return positive(f.liveHours) || positive(f.recordedHours);
  if (key === 'weekly') return positive(f.sessionsPerWeek) && positive(f.sessionMinutes);
  if (key === 'format' || key === 'level' || key === 'language') return has(f[key]);
  return false;
}

/** The facts-strip tiles: the builder's keys (known, no repeats, never `time`) that have data, in its order. */
export function visibleFactKeys(facts: ApplyFacts | null | undefined, keys: readonly string[]): HeaderFactKey[] {
  const out: HeaderFactKey[] = [];
  const list = arr(keys);
  for (let i = 0; i < list.length; i++) {
    const k = list[i];
    if (k !== 'time' && contains(HEADER_FACT_KEYS, k) && !contains(out, k) && factHasData(facts, k as string)) {
      out.push(k as HeaderFactKey);
    }
  }
  return out;
}

// ---- readiness (D.6, N.1.5) --------------------------------------------------------------------------
export function hasContact(variant: Pick<LeadFormVariant, 'questions'>): boolean {
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  for (let i = 0; i < qs.length; i++) if (qs[i].type === 'phone' || qs[i].type === 'email') return true;
  return false;
}

const LATIN_LETTER = /[A-Za-z\u00C0-\u024F]/;
const LITERAL_TEXT = /^(\S+@\S+\.\S+|(https?:\/\/|www\.)\S+)$/i;

/** An "Arabic" value that copies the English (same text, no Arabic letter). Text that is the same in both languages
 *  by nature is never flagged: no letter at all (digits, "+20 …", "—"), an e-mail address or a URL. */
export function isUntranslated(en: unknown, ar: unknown): boolean {
  const a = typeof ar === 'string' ? ar.trim() : '';
  const e = typeof en === 'string' ? en.trim() : '';
  return !!a && a === e && !ARABIC_LETTER.test(a) && LATIN_LETTER.test(a) && !LITERAL_TEXT.test(a);
}

/** Question types whose placeholder is a sample value (an address, a number, a date), the same in both languages. */
const LITERAL_PLACEHOLDER_TYPES: readonly string[] = ['email', 'phone', 'number', 'date'];

function activeVariants(c: LeadFormContent): { v: LeadFormVariant; i: number }[] {
  const out: { v: LeadFormVariant; i: number }[] = [];
  const vs = arr(c && c.variants) as LeadFormVariant[];
  for (let i = 0; i < vs.length; i++) if (vs[i].active) out.push({ v: vs[i], i });
  return out;
}

/**
 * Every check id appears once per level, in READINESS_*_IDS order; a failing check points at its first failure.
 * O.1.5: `ctx.formType === 'general'` uses the READINESS_GENERAL_*_IDS lists (no diploma checks, the header checks);
 * any other value (absent included) gives exactly the diploma lists and results.
 */
export function computeFormReadiness(c: LeadFormContent, ctx: ReadinessContext): FormReadiness {
  const general = !!ctx && ctx.formType === 'general';
  const checks: { [id: string]: ReadinessCheck } = {};
  const make = (ids: readonly string[], level: ReadinessCheck['level']) =>
    ids.map((id) => (checks[id] = { id, level, ok: true, path: '' }));
  const required = make(general ? READINESS_GENERAL_REQUIRED_IDS : READINESS_REQUIRED_IDS, 'required');
  const recommended = make(general ? READINESS_GENERAL_RECOMMENDED_IDS : READINESS_RECOMMENDED_IDS, 'recommended');
  const info = make(general ? READINESS_GENERAL_INFO_IDS : READINESS_INFO_IDS, 'info');
  const fail = (id: string, path: string, variantKey?: string, questionId?: string) => {
    const ch = own(checks, id) ? checks[id] : null;
    if (!ch || !ch.ok) return;
    ch.ok = false;
    ch.path = path;
    if (variantKey !== undefined) ch.variantKey = variantKey;
    if (questionId !== undefined) ch.questionId = questionId;
  };
  const pair = (id: string, o: Obj, field: string, path: string, vk?: string, qid?: string) => {
    if (!has(o[field])) fail(id, path + '.' + field, vk, qid);
    else if (!has(o[field + '_ar'])) fail(id, path + '.' + field + '_ar', vk, qid);
  };
  const untranslated = (o: Obj, field: string, path: string, vk?: string, qid?: string) => {
    if (isUntranslated(o[field], o[field + '_ar'])) fail('untranslated', path + '.' + field + '_ar', vk, qid);
  };
  const qTypes = (ctx && ctx.questionTypes) || {};
  const used = arr(ctx && ctx.usedVariantKeys);
  const liveKeys = arr(ctx && ctx.liveVariantKeys);
  const all = arr(c && c.variants) as LeadFormVariant[];
  const active = activeVariants(c);

  if (!active.some((a) => a.v.weight > 0)) fail('has_variant', 'variants');
  for (let i = 0; i < all.length; i++) {
    const v = all[i];
    if (contains(used, v.key) && !contains(liveKeys, v.key)) fail('variant_keys', 'variants.' + i + '.key', v.key);
    const qs = arr(v.questions) as LeadFormQuestion[];
    for (let j = 0; j < qs.length; j++) {
      const locked = own(qTypes as Obj, qs[j].id) ? qTypes[qs[j].id] : null;
      if (locked && locked !== qs[j].type) {
        fail('type_lock', 'variants.' + i + '.questions.' + j + '.type', v.key, qs[j].id);
      }
    }
  }
  for (let a = 0; a < active.length; a++) {
    const v = active[a].v;
    const vp = 'variants.' + active[a].i;
    const qs = arr(v.questions) as LeadFormQuestion[];
    if (qs.length < 1 || qs.length > LIMITS.questions) fail('has_question', vp + '.questions', v.key);
    if (qs.length > 8) fail('many_questions', vp + '.questions', v.key);
    if (!hasContact(v)) fail('no_contact', vp + '.questions', v.key);
    const secs = arr(v.sections) as LeadFormSection[];
    if (!secs.length) fail('sections', vp + '.sections', v.key);
    let titled = 0;
    for (let k = 0; k < secs.length; k++) {
      const sec = secs[k];
      const sp = vp + '.sections.' + k;
      const so = sec as unknown as Obj;
      const n = arr(sec.questionIds).length;
      if (!n) fail('sections', sp + '.questionIds', v.key);
      if (has(sec.title) !== has(sec.title_ar)) fail('sections', sp + (has(sec.title) ? '.title_ar' : '.title'), v.key);
      if (has(sec.description) !== has(sec.description_ar)) {
        fail('sections', sp + (has(sec.description) ? '.description_ar' : '.description'), v.key);
      } else if (has(sec.description) && !isTitledSection(sec)) {
        fail('sections', sp + '.title', v.key);
      }
      if (isTitledSection(sec)) titled++;
      if (n > 6) fail('long_section', sp, v.key);
      untranslated(so, 'title', sp, v.key);
      untranslated(so, 'description', sp, v.key);
    }
    if (qs.length >= 4 && !titled) fail('untitled_sections', vp + '.sections.0', v.key);
    let phones = 0;
    let emails = 0;
    for (let j = 0; j < qs.length; j++) {
      const q = qs[j];
      const qp = vp + '.questions.' + j;
      const qo = q as unknown as Obj;
      pair('labels', qo, 'label', qp, v.key, q.id);
      untranslated(qo, 'label', qp, v.key, q.id);
      untranslated(qo, 'help', qp, v.key, q.id);
      if (LITERAL_PLACEHOLDER_TYPES.indexOf(q.type) < 0) untranslated(qo, 'placeholder', qp, v.key, q.id);
      if (q.type === 'phone' && ++phones > 1) fail('one_contact_each', qp, v.key, q.id);
      if (q.type === 'email' && ++emails > 1) fail('one_contact_each', qp, v.key, q.id);
      const opts = arr(q.options) as ChoiceOption[];
      if (isChoice(q.type)) {
        const max = q.type === 'dropdown' ? LIMITS.dropdownOptions : LIMITS.options;
        if (opts.length < 2 || opts.length > max) fail('choices', qp + '.options', v.key, q.id);
        const seenEn: Obj = {};
        const seenAr: Obj = {};
        for (let k = 0; k < opts.length; k++) {
          const op = qp + '.options.' + k;
          const oo = opts[k] as unknown as Obj;
          pair('labels', oo, 'label', op, v.key, q.id);
          untranslated(oo, 'label', op, v.key, q.id);
          const en = '~' + opts[k].label.trim().toLowerCase();
          const ar = '~' + opts[k].label_ar.trim();
          if ((en !== '~' && seenEn[en]) || (ar !== '~' && seenAr[ar])) fail('choices', op + '.label', v.key, q.id);
          seenEn[en] = 1;
          seenAr[ar] = 1;
        }
      }
      if (q.type === 'multi_choice') {
        const lo = q.minSelect;
        const hi = q.maxSelect;
        if ((lo !== null && hi !== null && lo > hi) || (hi !== null && hi > opts.length) ||
          (lo !== null && lo > opts.length)) {
          fail('bounds', qp + (lo !== null && hi !== null && lo > hi ? '.minSelect' : '.maxSelect'), v.key, q.id);
        }
      }
      if (q.type === 'number' && q.min !== null && q.max !== null && q.min > q.max) {
        fail('bounds', qp + '.min', v.key, q.id);
      }
      if (q.type === 'date' && q.minDate && q.maxDate && q.minDate > q.maxDate) {
        fail('bounds', qp + '.minDate', v.key, q.id);
      }
    }
  }
  const common = (c && c.common) || emptyCommon();
  const consent = common.consent as unknown as Obj;
  const marketing = common.consent.marketing as unknown as Obj;
  if (common.consent.mode === 'required') pair('consent_text', consent, 'text', 'common.consent');
  if (common.consent.marketing.show) pair('consent_text', marketing, 'text', 'common.consent.marketing');
  pair('thanks_text', common.thanks as unknown as Obj, 'title', 'common.thanks');
  untranslated(consent, 'text', 'common.consent');
  untranslated(marketing, 'text', 'common.consent.marketing');
  untranslated(common.thanks as unknown as Obj, 'title', 'common.thanks');
  untranslated(common.thanks as unknown as Obj, 'body', 'common.thanks');
  untranslated(common.closed as unknown as Obj, 'title', 'common.closed');
  untranslated(common.closed as unknown as Obj, 'body', 'common.closed');
  untranslated(common as unknown as Obj, 'submitLabel', 'common');
  const ctas = arr(common.thanks && common.thanks.ctas) as ThanksCta[];
  let enabled = 0;
  for (let k = 0; k < ctas.length; k++) {
    const cp = 'common.thanks.ctas.' + k;
    const co = ctas[k] as unknown as Obj;
    if (ctas[k].enabled) {
      enabled++;
      const problems = ctaProblems(ctas[k]);
      if (general && ctas[k].type === 'diploma_page') fail('ctas', cp + '.type');   // O.1.5: no diploma page
      else if (problems.length) fail('ctas', cp + '.' + problems[0].field);
    }
    untranslated(co, 'label', cp);
    untranslated(co, 'prefill', cp);
  }
  if (!enabled) fail('no_cta', 'common.thanks.ctas');
  if (general) {
    generalHeaderReadiness(common.generalHeader, fail, pair, untranslated);
  } else {
    if (!(ctx && ctx.diplomaActive)) fail('diploma_inactive', 'diploma');
    if (ctx && ctx.hasCover === false) fail('no_cover', 'common.header');
    if (ctx && ctx.facts && !visibleFactKeys(ctx.facts, arr(common.header && common.header.facts) as string[]).length) {
      fail('no_facts', 'common.header.facts');
    }
  }

  let ok = true;
  for (let i = 0; i < required.length; i++) if (!required[i].ok) ok = false;
  return { ok, required, recommended, info };
}

type FailFn = (id: string, path: string) => void;
type FieldFn = (id: string, o: Obj, field: string, path: string) => void;
type UntranslatedFn = (o: Obj, field: string, path: string) => void;

/** O.1.5: general_header (required), general_title (recommended), untranslated over the header, general_no_cover. */
function generalHeaderReadiness(raw: GeneralHeader | undefined, fail: FailFn, pair: FieldFn,
  untranslated: UntranslatedFn): void {
  const h = (isObj(raw) ? raw : emptyGeneralHeader()) as GeneralHeader;
  const ho = h as unknown as Obj;
  const hp = 'common.generalHeader';
  const titled = has(h.title) || has(h.title_ar);
  if (has(h.title) !== has(h.title_ar)) fail('general_header', hp + (has(h.title) ? '.title_ar' : '.title'));
  if (has(h.subtitle) !== has(h.subtitle_ar)) {
    fail('general_header', hp + (has(h.subtitle) ? '.subtitle_ar' : '.subtitle'));
  } else if (has(h.subtitle) && !titled) {
    fail('general_header', hp + '.title');
  }
  const facts = arr(h.facts) as GeneralFact[];
  if (facts.length > LIMITS.generalFacts) fail('general_header', hp + '.facts');
  for (let k = 0; k < facts.length; k++) {
    const fo = obj(facts[k]);
    const fp = hp + '.facts.' + k;
    pair('general_header', fo, 'label', fp);
    pair('general_header', fo, 'value', fp);
  }
  if (!titled) fail('general_title', hp + '.title');
  untranslated(ho, 'title', hp);
  untranslated(ho, 'subtitle', hp);
  for (let k = 0; k < facts.length; k++) {
    const fo = obj(facts[k]);
    untranslated(fo, 'label', hp + '.facts.' + k);
    untranslated(fo, 'value', hp + '.facts.' + k);
  }
  if (!has(h.cover)) fail('general_no_cover', hp + '.cover');
}

// ---- answers (B.7 answer rules; cheap checks first, so a public submit stays O(questions + options)) ----
const MAX_ANSWER_ERRORS = 25;
const NUMBER_TEXT_RE = /^-?\d+(\.\d+)?$/;

function isEmptyAnswer(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0);
}
function textCap(q: LeadFormQuestion): number {
  if (q.type === 'long_text') return Math.min(q.maxLength || LIMITS.longTextDefault, LIMITS.longTextCap);
  return Math.min(q.maxLength || LIMITS.shortTextDefault, LIMITS.shortTextCap);
}
/** The longest raw string each typed input may carry; over twice this is `too_long` before any other work. */
function rawCap(q: LeadFormQuestion): number {
  if (q.type === 'short_text' || q.type === 'long_text') return textCap(q);
  if (q.type === 'email') return 254;
  return 32; // phone, number, date
}
function cleanPhone(s: string): string {
  return normalizeDigits(s).replace(/[\s\-().]/g, '');
}
/** The B.7 email rule: trimmed, lower-cased, at most 254 characters, EMAIL_RE. */
function emailOk(s: string): boolean {
  const e = String(s).trim().toLowerCase();
  return e.length <= 254 && EMAIL_RE.test(e);
}
function numberValue(v: unknown): number | null {
  if (typeof v === 'number') return isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const t = normalizeDigits(v.trim());
  return NUMBER_TEXT_RE.test(t) ? Number(t) : null;
}
function realDate(s: string): boolean {
  if (!DATE_RE.test(s)) return false;
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7));
  const d = Number(s.slice(8, 10));
  if (m < 1 || m > 12 || d < 1) return false;
  const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];
  return d <= days;
}
function optionIndex(q: LeadFormQuestion, id: unknown): number {
  const opts = q.options || [];
  for (let i = 0; i < opts.length; i++) if (opts[i].id === id) return i;
  return -1;
}

/**
 * '' when the value is acceptable. Empty ('', [], null, missing) is `required` on a required question and valid on
 * an optional one. A value of the wrong shape for a text question is `not_allowed`.
 */
export function validateAnswer(q: LeadFormQuestion, v: unknown): '' | AnswerError {
  if (isEmptyAnswer(v)) return q.required ? 'required' : '';
  const t = q.type;
  if (typeof v === 'string' && t !== 'single_choice' && t !== 'dropdown' && v.length > rawCap(q) * 2) {
    return 'too_long';
  }
  if (t === 'short_text' || t === 'long_text') {
    if (typeof v !== 'string') return 'not_allowed';
    return v.trim().length > textCap(q) ? 'too_long' : '';
  }
  if (t === 'single_choice' || t === 'dropdown') {
    return typeof v === 'string' && optionIndex(q, v) >= 0 ? '' : 'not_an_option';
  }
  if (t === 'multi_choice') {
    if (!Array.isArray(v)) return 'not_an_option';
    const opts = q.options || [];
    if (v.length > opts.length) return 'too_many';
    const seen: Obj = {};
    for (let i = 0; i < v.length; i++) {
      const id = v[i];
      if (typeof id !== 'string' || optionIndex(q, id) < 0) return 'not_an_option';
      if (seen['~' + id]) return 'not_an_option';
      seen['~' + id] = 1;
    }
    const lo = q.minSelect !== null && q.minSelect !== undefined ? q.minSelect : q.required ? 1 : 0;
    const hi = q.maxSelect !== null && q.maxSelect !== undefined ? q.maxSelect : opts.length;
    if (v.length < lo) return 'too_few';
    if (v.length > hi) return 'too_many';
    return '';
  }
  if (t === 'yes_no') return typeof v === 'boolean' ? '' : 'not_an_option';
  if (t === 'phone') {
    // P.1.4: E.164, then (unless the question accepts any number) a mobile number where the country has a rule.
    // The wire reason stays invalid_phone; the FE picks its "mobile" copy locally.
    const c = typeof v === 'string' ? cleanPhone(v) : '';
    if (!E164_RE.test(c)) return 'invalid_phone';
    return q.phoneMode !== 'any' && !isMobileE164(c) ? 'invalid_phone' : '';
  }
  if (t === 'email') {
    if (typeof v !== 'string') return 'invalid_email';
    // P.1.6: a malformed email blocks Send only on a required question; an optional one keeps the typed text.
    return emailOk(v) || !q.required ? '' : 'invalid_email';
  }
  if (t === 'number') {
    const n = numberValue(v);
    if (n === null || (q.integer && Math.floor(n) !== n)) return 'invalid_number';
    if ((q.min !== null && q.min !== undefined && n < q.min) || (q.max !== null && q.max !== undefined && n > q.max)) {
      return 'out_of_range';
    }
    return '';
  }
  if (t === 'date') {
    if (typeof v !== 'string') return 'invalid_date';
    const d = normalizeDigits(v.trim());
    if (!realDate(d)) return 'invalid_date';
    if ((q.minDate && d < q.minDate) || (q.maxDate && d > q.maxDate)) return 'out_of_range';
    return '';
  }
  return 'not_allowed';
}

/** The stored value of a valid answer: trimmed text, an E.164 phone, a lower-cased email, a number, a date. */
function cleanValue(q: LeadFormQuestion, v: unknown): AnswerValue | null {
  if (isEmptyAnswer(v)) return null;
  switch (q.type) {
    case 'short_text':
    case 'long_text':
      return String(v).trim();
    case 'phone':
      return cleanPhone(String(v));
    case 'email':
      return emailOk(String(v)) ? String(v).trim().toLowerCase() : null; // P.1.6: malformed optional → null
    case 'number':
      return numberValue(v);
    case 'date':
      return normalizeDigits(String(v).trim());
    case 'multi_choice':
      return (v as string[]).slice();
    default:
      return v as AnswerValue;
  }
}

function optionText(q: LeadFormQuestion, id: unknown): string {
  const i = optionIndex(q, id);
  if (i < 0) return String(id);
  const o = q.options[i];
  return o.label || o.label_ar || o.id;
}

/** The human text of an answer (inbox, CSV): option labels (EN, else AR), Yes/No, other values as typed. */
export function answerText(q: LeadFormQuestion, v: AnswerValue | null | undefined): string {
  if (v === null || v === undefined) return '';
  if (q.type === 'yes_no') return v === true ? 'Yes' : v === false ? 'No' : String(v);
  if (q.type === 'multi_choice' && Array.isArray(v)) return v.map((id) => optionText(q, id)).join(', ');
  if (q.type === 'single_choice' || q.type === 'dropdown') return optionText(q, v);
  return Array.isArray(v) ? v.join(', ') : String(v);
}

/**
 * Authoritative on the BE, and run by the FE on Send (over every question of the one page, N.3.6). An unknown key is `not_allowed`; missing consent
 * under `required` is {questionId:'_consent', reason:'required'}. `clean` holds the label snapshots, in order.
 */
export function validateAnswers(
  variant: Pick<LeadFormVariant, 'questions'>,
  answers: { [qid: string]: AnswerValue } | null | undefined,
  consentMode: ConsentMode,
  consent: boolean,
): ValidateAnswersResult {
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  const given = obj(answers);
  const errors: AnswerIssue[] = [];
  const add = (questionId: string, reason: AnswerError) => {
    if (errors.length < MAX_ANSWER_ERRORS) errors.push({ questionId, reason });
  };
  let count = 0;
  for (const k in given) if (own(given, k)) count++;
  if (count > qs.length) {
    return { ok: false, errors: [{ questionId: '_answers', reason: 'not_allowed' }], clean: [] };
  }
  const known: Obj = {};
  for (let i = 0; i < qs.length; i++) known['~' + qs[i].id] = 1;
  for (const k in given) if (own(given, k) && !known['~' + k]) add(k, 'not_allowed');
  const clean: LeadAnswer[] = [];
  for (let i = 0; i < qs.length; i++) {
    const q = qs[i];
    const raw = own(given, q.id) ? given[q.id] : undefined;
    const reason = validateAnswer(q, raw);
    if (reason) {
      add(q.id, reason);
      continue;
    }
    const v = cleanValue(q, raw);
    // P.1.6: a malformed optional email keeps v null (never a contact, a dedup key or CAPI data) and the typed text,
    // so sales can still read it.
    const text = q.type === 'email' && v === null && typeof raw === 'string' ? raw.trim().slice(0, 254) : answerText(q, v);
    clean.push({ q: q.id, t: q.type, label: q.label, label_ar: q.label_ar, v, text });
  }
  if (consentMode === 'required' && consent !== true) add('_consent', 'required');
  return { ok: errors.length === 0, errors, clean };
}

/** The one phone (E.164), the one email (lower-cased) and the first short_text marked as the name. */
export function extractContact(variant: Pick<LeadFormVariant, 'questions'>, clean: LeadAnswer[]): LeadContact {
  const out: LeadContact = { name: '', phone: '', email: '' };
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  const byQ: { [k: string]: LeadAnswer } = {};
  const answers = arr(clean) as LeadAnswer[];
  for (let i = 0; i < answers.length; i++) byQ['~' + answers[i].q] = answers[i];
  for (let i = 0; i < qs.length; i++) {
    const q = qs[i];
    const a = byQ['~' + q.id];
    const v = a && typeof a.v === 'string' ? a.v : '';
    if (!v) continue;
    if (q.type === 'phone' && !out.phone) out.phone = v;
    else if (q.type === 'email' && !out.email) out.email = v.toLowerCase();
    else if (q.type === 'short_text' && q.semantic === 'name' && !out.name) out.name = v;
  }
  return out;
}

/** {first, last}: the first token, then the rest. */
export function splitName(full: string): { first: string; last: string } {
  const parts = String(full || '').trim().split(/\s+/).filter((p) => !!p);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') };
}

/** 6 s per choice or yes/no, 10 s per short input, 25 s per long text. */
export function estimateSeconds(variant: Pick<LeadFormVariant, 'questions'>): number {
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  let s = 0;
  for (let i = 0; i < qs.length; i++) {
    const t = qs[i].type;
    s += t === 'long_text' ? 25 : isChoice(t) || t === 'yes_no' ? 6 : 10;
  }
  return s;
}

// ---- the one page (N.3.6): progress rail, section state, issues in page order -------------------------
type Answers = { [qid: string]: AnswerValue } | null | undefined;

/** N.4.2 "answered": a non-empty value that passes validateAnswer (an empty optional answer is not answered). */
export function isAnswered(q: LeadFormQuestion, v: unknown): boolean {
  return !isEmptyAnswer(v) && validateAnswer(q, v) === '';
}

/** The rail: answered required / required questions; with none required, answered / all. `done` = all counted. */
export function formProgress(variant: Pick<LeadFormVariant, 'questions'>, answers: Answers): FormProgress {
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  const given = obj(answers);
  let req = 0;
  for (let i = 0; i < qs.length; i++) if (qs[i].required) req++;
  const requiredOnly = req > 0;
  let total = 0;
  let answered = 0;
  for (let i = 0; i < qs.length; i++) {
    if (requiredOnly && !qs[i].required) continue;
    total++;
    if (isAnswered(qs[i], own(given, qs[i].id) ? given[qs[i].id] : undefined)) answered++;
  }
  return { answered, total, requiredOnly, done: total > 0 && answered === total };
}

/**
 * One state per section, in page order: the marker number (titled sections only, counted 1, 2, 3; a variant with a
 * single titled section shows no number), complete (every required question answered, or every question when the
 * section has none required) and the count of `issues` that point at its questions (the error badge).
 */
export function sectionStates(
  variant: { questions: LeadFormQuestion[]; sections?: LeadFormSection[] },
  answers: Answers,
  issues?: AnswerIssue[] | null,
): SectionState[] {
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  const secs = arr(variant && variant.sections) as LeadFormSection[];
  const given = obj(answers);
  const byId: { [k: string]: LeadFormQuestion } = {};
  for (let i = 0; i < qs.length; i++) if (!byId['~' + qs[i].id]) byId['~' + qs[i].id] = qs[i];
  const errs: { [k: string]: number } = {};
  const list = arr(issues) as AnswerIssue[];
  for (let i = 0; i < list.length; i++) errs['~' + list[i].questionId] = (errs['~' + list[i].questionId] || 0) + 1;
  let titledTotal = 0;
  for (let i = 0; i < secs.length; i++) if (isTitledSection(secs[i])) titledTotal++;
  const out: SectionState[] = [];
  let num = 0;
  for (let i = 0; i < secs.length; i++) {
    const titled = isTitledSection(secs[i]);
    if (titled) num++;
    const ids = arr(secs[i].questionIds) as string[];
    let questions = 0;
    let required = 0;
    let answered = 0;
    let answeredRequired = 0;
    let errors = 0;
    for (let j = 0; j < ids.length; j++) {
      const q = byId['~' + ids[j]];
      if (!q) continue;
      questions++;
      errors += errs['~' + q.id] || 0;
      const ok = isAnswered(q, own(given, q.id) ? given[q.id] : undefined);
      if (q.required) required++;
      if (ok) answered++;
      if (ok && q.required) answeredRequired++;
    }
    out.push({
      id: secs[i].id, index: i, titled, number: titled && titledTotal > 1 ? num : null, questions, required, answered,
      complete: questions > 0 && (required > 0 ? answeredRequired === required : answered === questions), errors,
    });
  }
  return out;
}

/**
 * validateAnswers errors in page order with their question index and section (the first one is where the page
 * scrolls on a failed submit); `_consent`, `_answers` and unknown qids follow, with index -1 and section ''.
 */
export function pageIssues(
  variant: { questions: LeadFormQuestion[]; sections?: LeadFormSection[] },
  issues: AnswerIssue[] | null | undefined,
): PageIssue[] {
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  const list = arr(issues) as AnswerIssue[];
  const byQ: { [k: string]: AnswerIssue[] } = {};
  for (let i = 0; i < list.length; i++) (byQ['~' + list[i].questionId] = byQ['~' + list[i].questionId] || []).push(list[i]);
  const out: PageIssue[] = [];
  const done: Obj = {};
  for (let i = 0; i < qs.length; i++) {
    const group = byQ['~' + qs[i].id];
    if (!group || done['~' + qs[i].id]) continue;
    done['~' + qs[i].id] = 1;
    const section = sectionOf(variant, qs[i].id);
    for (let g = 0; g < group.length; g++) {
      out.push({ questionId: group[g].questionId, reason: group[g].reason, index: i, section });
    }
  }
  for (let i = 0; i < list.length; i++) {
    if (!done['~' + list[i].questionId]) out.push({ questionId: list[i].questionId, reason: list[i].reason, index: -1, section: '' });
  }
  return out;
}

// ---- funnel maths (G.1, G.2, N.4). Rates named *Pct are percentages with one decimal; *Rate and rate are 0..1 ----
/** Not completed and no activity for inProgressMs (30 min). Activity exactly 30 min ago is still in progress. */
export function isQuit(s: QuitSessionLike, now: Instant): boolean {
  if (!s || s.completedAt) return false;
  return toMs(s.lastActivityAt) < toMs(now) - LIMITS.inProgressMs;
}

/**
 * The median of a bucketed distribution, interpolated inside the median bucket. `edges` are the lower edges
 * (FUNNEL_BUCKETS_MS by default); the last bucket is open, so a median there is its lower edge. null when empty.
 */
export function medianFromBuckets(b: number[], edges?: number[]): number | null {
  const e = edges && edges.length ? edges : FUNNEL_BUCKETS_MS;
  const counts = arr(b).map((x) => (typeof x === 'number' && x > 0 ? x : 0));
  let n = 0;
  for (let i = 0; i < counts.length; i++) n += counts[i];
  if (n === 0) return null;
  const half = n / 2;
  let cum = 0;
  for (let i = 0; i < counts.length; i++) {
    const c = counts[i];
    if (c > 0 && cum + c >= half) {
      const lo = i < e.length ? e[i] : e[e.length - 1];
      if (i + 1 >= e.length) return lo;
      return Math.round(lo + (e[i + 1] - lo) * ((half - cum) / c));
    }
    cum += c;
  }
  return e[e.length - 1];
}

/** The index of the first REQUIRED question after index `m` (-1 = before the first question); -1 when none. */
export function nextRequiredIndex(variant: Pick<LeadFormVariant, 'questions'>, m: number): number {
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  const from = typeof m === 'number' && isFinite(m) ? Math.max(0, Math.floor(m) + 1) : 0;
  for (let i = from; i < qs.length; i++) if (qs[i].required) return i;
  return -1;
}

/**
 * N.4.2 (2 Oct): where one left, started one-page session stopped, from its answer pattern `p` (one char per question
 * of the version, page order: 'a' answered, 'r' reached and left empty, anything else or a missing char = not
 * reached). People answer out of order on one page, so the furthest answer is not where they stopped: a question
 * ABOVE the furthest card they reached or answered counts as passed (they scrolled past it; a fast flick to the
 * bottom registers only the cards it rests on, so a gap between reached cards is not "not reached"). Returns
 *   -2  nothing reached or answered: left before starting (counted with `bounced`);
 *   -1  every required question answered (quitAtSubmit: consent, Send or a failed request stopped them);
 *   otherwise the index of the first unanswered REQUIRED question they reached or passed (e.g. a skipped phone above
 *   later answers or above a card they scrolled to); with none, the first reached, unanswered question after their
 *   last answer (they saw it and left; it may be optional); with none, the first unanswered required question (not
 *   reached yet: they left before it).
 */
export function stopIndex(qs: Pick<LeadFormQuestion, 'required'>[], p: string): number {
  const list = arr(qs) as LeadFormQuestion[];
  const s = typeof p === 'string' ? p : '';
  let last = -1;
  let far = -1;
  for (let i = 0; i < list.length; i++) {
    const c = s.charAt(i);
    if (c === 'a') last = i;
    if (c === 'a' || c === 'r') far = i;
  }
  if (far < 0) return -2;
  let firstReq = -1;
  let firstReqSeen = -1;
  for (let i = 0; i < list.length; i++) {
    const c = s.charAt(i);
    if (c === 'a' || !list[i].required) continue;
    if (firstReq < 0) firstReq = i;
    if (firstReqSeen < 0 && (c === 'r' || i < far)) firstReqSeen = i;
  }
  if (firstReq < 0) return -1;
  if (firstReqSeen >= 0) return firstReqSeen;
  for (let i = last + 1; i < list.length; i++) if (s.charAt(i) === 'r') return i;
  return firstReq;
}

/** N.4.2 "Skipped": the indices left empty (reached or scrolled past) while a LATER question was answered. */
export function skippedIndices(p: string): number[] {
  const s = typeof p === 'string' ? p : '';
  const last = s.lastIndexOf('a');
  const out: number[] = [];
  for (let i = 0; i < last; i++) if (s.charAt(i) !== 'a') out.push(i);
  return out;
}

/**
 * N.4.2 (layout 2): the stop of each started, left session and the per-question skips. With `agg.patterns` (2 Oct):
 * stopIndex / skippedIndices per pattern (skips over every started session, sent or not). Without it (an aggregate
 * that predates the patterns): TF3, the next required question after the session's furthest answer (maxAnswered).
 */
function stopSplit(qs: LeadFormQuestion[], agg: FunnelAgg) {
  const quitAt: { [k: string]: number } = {};
  const lastAt: { [k: string]: number } = {};
  const skipped: { [k: string]: number } = {};
  let quitAtSubmit = 0;
  let beforeStart = 0;
  const list = arr(agg && agg.lastAnswered) as { index: number; n: number }[];
  const byPattern = !!agg && Array.isArray(agg.patterns);
  for (let i = 0; i < list.length; i++) {
    const m = list[i].index;
    const n = list[i].n > 0 ? list[i].n : 0;
    lastAt['~' + m] = (lastAt['~' + m] || 0) + n;
    if (byPattern) continue;
    const j = nextRequiredIndex({ questions: qs }, m);
    if (j < 0) quitAtSubmit += n;
    else quitAt['~' + j] = (quitAt['~' + j] || 0) + n;
  }
  const pats = arr(agg && agg.patterns) as { p: string; left: boolean; n: number }[];
  for (let i = 0; i < pats.length; i++) {
    const n = pats[i] && pats[i].n > 0 ? pats[i].n : 0;
    if (!n) continue;
    const sk = skippedIndices(pats[i].p);
    for (let k = 0; k < sk.length; k++) skipped['~' + sk[k]] = (skipped['~' + sk[k]] || 0) + n;
    if (!pats[i].left) continue;
    const j = stopIndex(qs, pats[i].p);
    if (j === -1) quitAtSubmit += n;
    else if (j === -2) beforeStart += n;
    else quitAt['~' + j] = (quitAt['~' + j] || 0) + n;
  }
  return { quitAt, lastAt, quitAtSubmit, beforeStart, skipped };
}

/**
 * Maps the `steps` facet onto the version's question order. qids with data that are not in that order are
 * appended as `removed` (their labels come from agg.questions when the BE knows them).
 * Layout 1 (`agg.layout` absent): G.1, quit = sessions whose maxIndex is the step, arrived = reached.
 * Layout 2 (N.4.2): quit ("Stopped here") = started left sessions whose stop (stopIndex, or the TF3 maxAnswered rule
 * without `agg.patterns`) is this question; skipped = sessions that left it empty while answering a later one;
 * arrived = completed + quitAtSubmit + the quits at this row and after; dropPct = quit / arrived, nextPct = 100 −
 * dropPct. Both fill `section`, `required` and `lastAnswered` (skipped is 0 for layout 1).
 */
export function funnelRows(
  variant: { questions: LeadFormQuestion[]; sections?: LeadFormSection[] },
  agg: FunnelAgg,
  now?: Instant,
): FunnelStepRow[] {
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  const layout2 = !!agg && agg.layout === 2;
  const steps = arr(agg && agg.steps) as FunnelStepAgg[];
  const byQ: { [k: string]: FunnelStepAgg } = {};
  for (let i = 0; i < steps.length; i++) byQ['~' + steps[i].q] = steps[i];
  const split = stopSplit(qs, agg);
  let quitAt: { [k: string]: number } = split.quitAt;
  if (!layout2) {
    quitAt = {};
    const quits = arr(agg && agg.quits) as { index: number; n: number }[];
    for (let i = 0; i < quits.length; i++) quitAt['~' + quits[i].index] = (quitAt['~' + quits[i].index] || 0) + quits[i].n;
  }
  const rows: FunnelStepRow[] = [];
  const inOrder: Obj = {};
  for (let i = 0; i < qs.length; i++) {
    const q = qs[i];
    const s = byQ['~' + q.id];
    inOrder['~' + q.id] = 1;
    const reached = s ? s.reached : 0;
    const quit = quitAt['~' + i] || 0;
    rows.push({
      q: q.id, index: i, label: q.label, label_ar: q.label_ar, type: q.type, removed: false,
      reached, answered: s ? s.answered : 0, quit, dropPct: layout2 ? 0 : pct(quit, reached), nextPct: 0,
      medianMs: s ? medianFromBuckets(s.buckets) : null,
      section: sectionOf(variant, q.id), required: !!q.required, lastAnswered: split.lastAt['~' + i] || 0,
      arrived: layout2 ? 0 : reached, skipped: layout2 ? split.skipped['~' + i] || 0 : 0,
    });
  }
  const completed = (agg && agg.completed) || 0;
  if (layout2) {
    let acc = completed + split.quitAtSubmit;
    for (let i = rows.length - 1; i >= 0; i--) {
      acc += rows[i].quit;
      rows[i].arrived = acc;
    }
    for (let i = 0; i < rows.length; i++) {
      rows[i].dropPct = pct(rows[i].quit, rows[i].arrived);
      rows[i].nextPct = rows[i].arrived > 0 ? round1(100 - rows[i].dropPct) : 0;
    }
  } else {
    for (let i = 0; i < rows.length; i++) {
      rows[i].nextPct = pct(i + 1 < rows.length ? rows[i + 1].reached : completed, rows[i].reached);
    }
  }
  const meta = (agg && agg.questions) || {};
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    if (inOrder['~' + s.q]) continue;
    const m = own(meta as Obj, s.q) ? meta[s.q] : null;
    rows.push({
      q: s.q, index: rows.length, label: m ? m.label : '', label_ar: m ? m.label_ar : '',
      type: m ? m.type : 'short_text', removed: true, reached: s.reached, answered: s.answered, quit: 0,
      dropPct: 0, nextPct: 0, medianMs: medianFromBuckets(s.buckets),
      section: '', required: false, lastAnswered: 0, arrived: layout2 ? 0 : s.reached, skipped: 0,
    });
  }
  return rows;
}

/**
 * The layout-2 KPI tiles (N.4.2): bounced = left without any interaction (agg.bounced, from the facet) plus the
 * started sessions that reached no question (stopIndex −2); quitAtSubmit = started, left, every required question
 * answered (with `agg.patterns`; without: none left after their furthest answer). Both null for layout 1.
 */
export function funnelQuitTotals(
  variant: Pick<LeadFormVariant, 'questions'>,
  agg: FunnelAgg,
): { bounced: number | null; quitAtSubmit: number | null } {
  if (!agg || agg.layout !== 2) return { bounced: null, quitAtSubmit: null };
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  const split = stopSplit(qs, agg);
  return { bounced: (agg.bounced && agg.bounced > 0 ? agg.bounced : 0) + split.beforeStart, quitAtSubmit: split.quitAtSubmit };
}

/**
 * N.4.4 section rows (layout 2), in the version's section order: reached/completed from the `sections` facet
 * (matched by id), arrived = arrived of the section's FIRST question in page order (the lowest index; 2 Oct: optional
 * questions get stops too, and arrived is a sum from that row on, so it includes every quit of the section),
 * quit = the sum of its questions' quits, dropPct = quit / arrived.
 */
export function sectionRows(
  variant: { questions: LeadFormQuestion[]; sections?: LeadFormSection[] },
  agg: FunnelAgg,
  rows: FunnelStepRow[],
): FunnelSectionRow[] {
  const secs = arr(variant && variant.sections) as LeadFormSection[];
  const facet: { [k: string]: { reached: number; completed: number } } = {};
  const list = arr(agg && agg.sections) as { id: string; reached: number; completed: number }[];
  for (let i = 0; i < list.length; i++) facet['~' + list[i].id] = list[i];
  const byQ: { [k: string]: FunnelStepRow } = {};
  const rs = arr(rows) as FunnelStepRow[];
  for (let i = 0; i < rs.length; i++) if (!rs[i].removed) byQ['~' + rs[i].q] = rs[i];
  const out: FunnelSectionRow[] = [];
  for (let i = 0; i < secs.length; i++) {
    const ids = arr(secs[i].questionIds) as string[];
    let quit = 0;
    let head: FunnelStepRow | null = null;
    for (let j = 0; j < ids.length; j++) {
      const r = byQ['~' + ids[j]];
      if (!r) continue;
      quit += r.quit;
      if (!head || r.index < head.index) head = r;
    }
    const arrived = head ? head.arrived : 0;
    const f = facet['~' + secs[i].id];
    out.push({
      id: secs[i].id, index: i, title: secs[i].title, title_ar: secs[i].title_ar, questions: ids.length,
      reached: f ? f.reached : 0, arrived, completed: f ? f.completed : 0, quit, dropPct: pct(quit, arrived),
    });
  }
  return out;
}

/**
 * G.1 / N.4.2: the in-order step with the highest dropPct among steps with at least 20 sessions in its denominator
 * (`arrived`: = reached for layout 1, the by-stops count for layout 2). Every row competes: since 2 Oct a layout-2
 * optional row has stops too (the reached, empty question after the last answer, stopIndex).
 */
export function biggestDrop(rows: FunnelStepRow[]): { q: string; label: string; dropPct: number } | null {
  let best: FunnelStepRow | null = null;
  const list = arr(rows) as FunnelStepRow[];
  for (let i = 0; i < list.length; i++) {
    const r = list[i];
    const base = typeof r.arrived === 'number' ? r.arrived : r.reached;
    if (r.removed || base < BIGGEST_DROP_MIN_REACHED) continue;
    if (!best || r.dropPct > best.dropPct) best = r;
  }
  return best && best.dropPct > 0 ? { q: best.q, label: best.label, dropPct: best.dropPct } : null;
}

// ---- A/B maths (G.4) ----------------------------------------------------------------------------------
/** Abramowitz-Stegun 7.1.26 (|error| < 1.5e-7). */
export function erf(x: number): number {
  const sign = x < 0 ? -1 : 1;
  const a = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * a);
  const y = 1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t *
    Math.exp(-a * a);
  return sign * y;
}
export function normalCdf(z: number): number {
  return 0.5 * (1 + erf(z / Math.SQRT2));
}

/** Wilson 95% interval of c successes in n (0..1). */
export function wilson(c: number, n: number): { low: number; high: number } {
  if (!(n > 0)) return { low: 0, high: 0 };
  const z = 1.96;
  const p = c / n;
  const z2 = z * z;
  const denom = 1 + z2 / n;
  const centre = (p + z2 / (2 * n)) / denom;
  const half = (z * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n))) / denom;
  return { low: Math.max(0, centre - half), high: Math.min(1, centre + half) };
}

/** Pooled two-proportion z-test, two-sided. diff = p2 - p1. */
export function twoProportion(c1: number, n1: number, c2: number, n2: number): TwoProportionResult {
  const p1 = n1 > 0 ? c1 / n1 : 0;
  const p2 = n2 > 0 ? c2 / n2 : 0;
  const diff = p2 - p1;
  const pooled = n1 + n2 > 0 ? (c1 + c2) / (n1 + n2) : 0;
  const se = n1 > 0 && n2 > 0 ? Math.sqrt(pooled * (1 - pooled) * (1 / n1 + 1 / n2)) : 0;
  if (!(se > 0)) return { p1, p2, diff, z: 0, p: 1 };
  const z = diff / se;
  const p = Math.min(1, Math.max(0, 2 * (1 - normalCdf(Math.abs(z)))));
  return { p1, p2, diff, z, p };
}

/** Visits per variant for 80% power at alpha .05: ceil(7.85 (pC(1-pC) + pV(1-pV)) / delta^2). */
export function neededPerVariant(pC: number, pV: number): number {
  const delta = Math.max(Math.abs(pV - pC), 0.1 * pC, 0.01);
  return Math.ceil((7.85 * (pC * (1 - pC) + pV * (1 - pV))) / (delta * delta));
}

/**
 * Each variant against the control (`a`, else the first row). alpha defaults to Bonferroni 0.05/(k-1).
 * The control row carries no diff, p, verdict or "more visits".
 */
export function abVerdict(rows: AbInputRow[], alpha?: number): { control: string; alpha: number; rows: AbVariantRow[] } {
  const list = arr(rows) as AbInputRow[];
  let ci = 0;
  for (let i = 0; i < list.length; i++) if (list[i].key === 'a') ci = i;
  const k = list.length;
  const a = typeof alpha === 'number' && alpha > 0 ? alpha : 0.05 / Math.max(1, k - 1);
  const ctl = list[ci];
  const out: AbVariantRow[] = [];
  for (let i = 0; i < list.length; i++) {
    const r = list[i];
    const n = r.opened > 0 ? r.opened : 0;
    const c = r.completed > 0 ? r.completed : 0;
    const w = wilson(c, n);
    const row: AbVariantRow = {
      key: r.key, name: r.name, version: r.version, since: r.since, weight: r.weight, opened: n, completed: c,
      rate: n > 0 ? c / n : 0, ciLow: w.low, ciHigh: w.high, diffPts: null, p: null, verdict: null,
      moreVisitsNeeded: null,
    };
    if (i !== ci && ctl) {
      const nC = ctl.opened > 0 ? ctl.opened : 0;
      const cC = ctl.completed > 0 ? ctl.completed : 0;
      const tp = twoProportion(cC, nC, c, n);
      const needed = neededPerVariant(tp.p1, tp.p2);
      const nMin = Math.min(nC, n);
      let verdict: AbVerdict;
      let more: number | null = null;
      if (nMin < 100 || Math.min(cC, c) < 10) {
        verdict = 'not_enough';
        more = Math.max(100 - nMin, needed - nMin, 0);
      } else if (tp.p < a) {
        verdict = tp.diff > 0 ? 'better' : 'worse';
      } else if (nMin >= needed) {
        verdict = 'no_difference';
      } else {
        verdict = 'keep_running';
        more = needed - nMin;
      }
      row.diffPts = round1(tp.diff * 100);
      row.p = nC > 0 && n > 0 ? tp.p : null;
      row.verdict = verdict;
      row.moreVisitsNeeded = more;
    }
    out.push(row);
  }
  return { control: ctl ? ctl.key : '', alpha: a, rows: out };
}

/**
 * Sample-ratio mismatch: chi-square goodness of fit of distinct-vid counts against the weights, over variants with
 * weight > 0 only (df = k - 1, p by the Wilson-Hilferty approximation). warning when p < 0.001.
 */
export function srmCheck(counts: { [key: string]: number }, weights: { [key: string]: number }): SrmResult {
  const keys: string[] = [];
  const w = obj(weights);
  const cnt = obj(counts);
  for (const k in w) if (own(w, k) && typeof w[k] === 'number' && (w[k] as number) > 0) keys.push(k);
  if (keys.length < 2) return { p: null, warning: false };
  let total = 0;
  let wTotal = 0;
  for (let i = 0; i < keys.length; i++) {
    const o = cnt[keys[i]];
    total += typeof o === 'number' && o > 0 ? o : 0;
    wTotal += w[keys[i]] as number;
  }
  if (total === 0) return { p: null, warning: false };
  let chi2 = 0;
  for (let i = 0; i < keys.length; i++) {
    const o = cnt[keys[i]];
    const obs = typeof o === 'number' && o > 0 ? o : 0;
    const exp = (total * (w[keys[i]] as number)) / wTotal;
    chi2 += ((obs - exp) * (obs - exp)) / exp;
  }
  const df = keys.length - 1;
  const v = 2 / (9 * df);
  const z = (Math.pow(chi2 / df, 1 / 3) - (1 - v)) / Math.sqrt(v);
  const p = Math.min(1, Math.max(0, 1 - normalCdf(z)));
  return { p, warning: p < SRM_P_THRESHOLD };
}

// ---- additions (P-01, 5 Oct: pay once per person, SPEC-P.1) ----
// The phone rule (P.1.4), the optional-email hint (P.1.6), the dedup keys and the merge of a repeat into the original
// lead (P.1.7), and the "Fix my number" route helpers (P.1.5). Pure: the BE stores, claims and writes; this decides.

/** '+201012345678' → {cc: '20', nsn: '1012345678'}: the first 1-3 digit prefix that has a MOBILE_NSN rule (E.164
 *  codes are prefix-free). null for a non-E.164 value or a calling code without a rule. */
export function splitE164(e164: string): { cc: string; nsn: string } | null {
  const s = String(e164 || '');
  if (!E164_RE.test(s)) return null;
  for (let n = 1; n <= 3; n++) {
    const cc = s.slice(1, 1 + n);
    if (own(MOBILE_NSN as Obj, cc)) return { cc, nsn: s.slice(1 + n) };
  }
  return null;
}
/** true when the number is a mobile of its country, or when its country has no rule (then only E.164 applies). */
export function isMobileE164(e164: string): boolean {
  const p = splitE164(e164);
  return p === null ? true : MOBILE_NSN[p.cc].test(p.nsn);
}
/** P.1.6: an optional email whose typed value fails the format: a non-blocking hint on the FE, never an error. */
export function softEmailIssue(q: LeadFormQuestion, v: unknown): boolean {
  return !!q && q.type === 'email' && !q.required && typeof v === 'string' && v.trim() !== '' && !emailOk(v);
}

/** The dedup scope: the diploma ('d:<id>') for a diploma form, the form ('f:<id>') for a general form. A staff
 *  (internal) submission gets its own partition ('s|…'): it never merges with, or absorbs, a real person (PD2). */
export function dedupScope(form: { type?: unknown; _id: unknown; diploma?: unknown }, internal: boolean): string {
  const base = formTypeOf(form) === 'general' ? 'f:' + String(form._id) : 'd:' + String(form.diploma);
  return (internal ? 's|' : '') + base;
}
/** The E.164 phone as a key, '' when the value is not E.164. */
export function phoneKey(v: unknown): string {
  const c = typeof v === 'string' ? cleanPhone(v) : '';
  return E164_RE.test(c) ? c : '';
}
/** The trimmed, lower-cased email as a key, '' when it fails the format. */
export function emailKey(v: unknown): string {
  return typeof v === 'string' && emailOk(v) ? v.trim().toLowerCase() : '';
}
/** The submission's keys in the order phone, email, vid, only the non-empty ones. P2: the vid always counts, except in
 *  a staff scope ('s|'), where one laptop types many clients (critic C7). */
export function dedupKeys(scope: string, contact: Partial<LeadContact> | null | undefined, vid: unknown): DedupKey[] {
  const c = obj(contact);
  const out: DedupKey[] = [];
  const p = phoneKey(c.phone);
  const e = emailKey(c.email);
  if (p) out.push({ kind: 'p', key: scope + '|p|' + p });
  if (e) out.push({ kind: 'e', key: scope + '|e|' + e });
  if (typeof vid === 'string' && UUID_V4_RE.test(vid) && String(scope).slice(0, 2) !== 's|') {
    out.push({ kind: 'v', key: scope + '|v|' + vid });
  }
  return out;
}
/** The kinds (in p, e, v order, once each) of this submission's keys that hit the chosen target. `ownedByTarget` holds
 *  the matching `key` strings (from the target's claims, or the target's own contact/vid fields). Stored on the
 *  submission entry as `matchedBy`, so the inbox can say "same browser only" (critic C6). */
export function matchKinds(keys: DedupKey[], ownedByTarget: string[]): DedupKind[] {
  const hit: Obj = {};
  const owned = arr(ownedByTarget);
  const list = arr(keys) as DedupKey[];
  for (let i = 0; i < list.length; i++) if (list[i] && contains(owned, list[i].key)) hit[list[i].kind] = 1;
  const out: DedupKind[] = [];
  const kinds: DedupKind[] = ['p', 'e', 'v'];
  for (let i = 0; i < kinds.length; i++) if (hit[kinds[i]]) out.push(kinds[i]);
  return out;
}

function carries(a: LeadAnswer): boolean {
  return a.v !== null && a.v !== undefined ? !isEmptyAnswer(a.v) : a.t === 'email' && typeof a.text === 'string' && a.text !== '';
}
/** P3 merge of a repeat's answers into the original's: start from `old` in its order; a `next` answer that carries
 *  something replaces the same question (when its value or text differs) or is appended; an empty `next` answer never
 *  erases. Returns at most LIMITS.changesPerSubmission changes; the merge itself is never cut. */
export function mergeAnswers(old: LeadAnswer[], next: LeadAnswer[]): { answers: LeadAnswer[]; changes: LeadChange[] } {
  const answers = (arr(old) as LeadAnswer[]).slice();
  const at: Obj = {};
  for (let i = 0; i < answers.length; i++) if (answers[i]) at['~' + answers[i].q] = i;
  const changes: LeadChange[] = [];
  const add = (c: LeadChange) => {
    if (changes.length < LIMITS.changesPerSubmission) changes.push(c);
  };
  const list = arr(next) as LeadAnswer[];
  for (let i = 0; i < list.length; i++) {
    const n = list[i];
    if (!n || !carries(n)) continue;
    const label = n.label || '';
    const labelAr = n.label_ar || '';
    if (own(at, '~' + n.q)) {
      const j = at['~' + n.q] as number;
      const o = answers[j];
      if (canonicalJson(o.v === undefined ? null : o.v) === canonicalJson(n.v === undefined ? null : n.v) &&
        (o.text || '') === (n.text || '')) continue;
      answers[j] = n;
      add({ q: n.q, label, label_ar: labelAr, from: o.text || '', to: n.text || '' });
    } else {
      at['~' + n.q] = answers.length;
      answers.push(n);
      add({ q: n.q, label, label_ar: labelAr, from: '', to: n.text || '' });
    }
  }
  return { answers, changes };
}

const CONTACT_PARTS: { k: 'name' | 'phone' | 'email'; q: string; label: string; label_ar: string }[] = [
  { k: 'name', q: '_name', label: 'Name', label_ar: 'الاسم' },
  { k: 'phone', q: '_phone', label: 'Phone', label_ar: 'الهاتف' },
  { k: 'email', q: '_email', label: 'Email', label_ar: 'البريد الإلكتروني' },
];
/** The newest contact: each part `next.x || old.x`, with a change per part that differs. `clear` (the "Fix it" edit
 *  only, P.2.6 / critic C8b) lists the parts the visitor emptied on purpose: those become '' instead of being kept. */
export function mergeContact(
  old: Partial<LeadContact> | null | undefined,
  next: Partial<LeadContact> | null | undefined,
  clear?: ('name' | 'phone' | 'email')[],
): { contact: LeadContact; changes: LeadChange[] } {
  const o = obj(old);
  const n = obj(next);
  const contact: LeadContact = { name: '', phone: '', email: '' };
  const changes: LeadChange[] = [];
  for (let i = 0; i < CONTACT_PARTS.length; i++) {
    const p = CONTACT_PARTS[i];
    const was = typeof o[p.k] === 'string' ? (o[p.k] as string) : '';
    const got = typeof n[p.k] === 'string' ? (n[p.k] as string) : '';
    const v = got || (contains(arr(clear), p.k) ? '' : was);
    contact[p.k] = v;
    if (v !== was) changes.push({ q: p.q, label: p.label, label_ar: p.label_ar, from: was, to: v });
  }
  return { contact, changes };
}
/** The older phones and emails of a lead (distinct, newest first), without the current ones and without ''. */
export function otherContacts(
  history: { phone?: string; email?: string }[] | null | undefined,
  current: Partial<LeadContact> | null | undefined,
): { phones: string[]; emails: string[] } {
  const c = obj(current);
  const h = arr(history);
  const phones: string[] = [];
  const emails: string[] = [];
  for (let i = h.length - 1; i >= 0; i--) {
    const e = obj(h[i]);
    const p = typeof e.phone === 'string' ? e.phone : '';
    const m = typeof e.email === 'string' ? e.email : '';
    if (p && p !== c.phone && !contains(phones, p)) phones.push(p);
    if (m && m !== c.email && !contains(emails, m)) emails.push(m);
  }
  return { phones, emails };
}

/** '/apply/<slug>/edit/' and '/form/<slug>/edit/' (locale-free, trailing slash; 'edit' is a reserved form key). */
export function editPath(slug: string): string {
  return '/apply/' + slug + '/edit/';
}
export function generalEditPath(slug: string): string {
  return '/form/' + slug + '/edit/';
}
/** The edit stash belongs to this page (stash slug and locale), has a token and has not expired. */
export function editValid(s: EditStashLike | null | undefined, stashSlug: string, locale: string, now: number): boolean {
  return !!s && s.v === 1 && s.slug === stashSlug && s.locale === locale && typeof s.exp === 'number' && now < s.exp &&
    typeof s.token === 'string' && s.token !== '';
}

// ---- additions (Q-01, 5 Oct: short links, SPEC-Q) ----
// Codes, aliases and tags of a short link (Q.1.1), its server-built target, the click filters (QD12) and the redirect
// merge (Q.3.3, QD4). Pure: the BE stores and resolves, the FE route redirects, the ADM shows; this decides.

const SHORT_TAG_KEYS = ['source', 'medium', 'campaign', 'content', 'term'];
const SHORT_SLUG_RE = /^[a-z0-9-]{1,80}$/;
const SHORT_TARGET_RE = /^\/(?:en\/)?(?:apply\/[a-z0-9-]{1,80}\/(?:[a-z0-9-]{1,40}\/)?|form\/[a-z0-9-]{1,80}\/)$/;
const GO_CLICK_ID_KEYS = ['fbclid', 'gclid', 'wbraid', 'gbraid'];
const PRINTABLE_PAIR_RE = /^[\x21-\x7E]+$/;

function safeDecode(s: string): string | null {
  try {
    return decodeURIComponent(s.replace(/\+/g, ' '));
  } catch (e) {
    return null;
  }
}
function safeEncode(s: string): string | null {
  try {
    return encodeURIComponent(s);
  } catch (e) {
    return null;
  }
}

export function emptyShortTags(): ShortLinkTags {
  return { source: '', medium: '', campaign: '', content: '', term: '' };
}
/** 'ABC234/' → 'abc234'. '' unless the result is a valid stored code (SHORT_CODE_RE). Never decodes '%'. */
export function normalizeShortCode(raw: unknown): string {
  if (typeof raw !== 'string' && typeof raw !== 'number') return '';
  const s = String(raw).trim().replace(/\/$/, '').toLowerCase();
  return SHORT_CODE_RE.test(s) ? s : '';
}
/** A custom name: 'reserved' (a site word or a reserved form key, checked first so that the 2-letter words 'go',
 *  'en', 'ar' read as reserved), 'invalid' (not a valid code) or ''. */
export function shortAliasProblem(raw: unknown): '' | 'invalid' | 'reserved' {
  const w = typeof raw === 'string' ? raw.trim().replace(/\/$/, '').toLowerCase() : '';
  if (contains(RESERVED_SHORT_CODES, w) || contains(RESERVED_FORM_KEYS, w)) return 'reserved';
  return normalizeShortCode(raw) ? '' : 'invalid';
}
/** `len` characters of SHORT_CODE_ALPHABET (31: no 0 o 1 l i); `rand` returns [0, 1). */
export function randomShortCode(rand: () => number, len: number): string {
  const n = SHORT_CODE_ALPHABET.length;
  let out = '';
  for (let i = 0; i < len; i++) out += SHORT_CODE_ALPHABET.charAt(Math.floor(rand() * n) % n);
  return out;
}
/** '/go/<code>/' (locale-free: the target fixes the locale). */
export function shortLinkPath(code: string): string {
  return '/go/' + code + '/';
}
/** The site's ISR tag of a code: 'diploma:go-<code>' (rides the landing tag grammar, QD3). */
export function shortLinkTag(code: string): string {
  return 'diploma:' + SHORT_LINK_TAG_PREFIX + code;
}
/** Stored tags (QD10): trimmed, lowercased slugs; a bad value is stored '' and reported as 'tags.<key>'. */
export function normalizeShortTags(raw: unknown): { tags: ShortLinkTags; problems: string[] } {
  const o = obj(raw);
  const tags = emptyShortTags() as unknown as Obj;
  const problems: string[] = [];
  for (let i = 0; i < SHORT_TAG_KEYS.length; i++) {
    const k = SHORT_TAG_KEYS[i];
    const v = typeof o[k] === 'string' ? (o[k] as string).trim().toLowerCase() : '';
    if (v === '') continue;
    if (SHORT_TAG_RE.test(v)) tags[k] = v;
    else problems.push('tags.' + k);
  }
  return { tags: tags as unknown as ShortLinkTags, problems };
}
/** 'utm_source=…&utm_medium=…' in the fixed order source, medium, campaign, content, term; empties skipped. */
export function shortTagQuery(tags: Partial<ShortLinkTags> | null | undefined): string {
  const o = obj(tags);
  const out: string[] = [];
  for (let i = 0; i < SHORT_TAG_KEYS.length; i++) {
    const v = o[SHORT_TAG_KEYS[i]];
    if (typeof v === 'string' && v !== '') out.push('utm_' + SHORT_TAG_KEYS[i] + '=' + v);
  }
  return out.join('&');
}
/** The locale-free target of a short link, built from the form only (never from the client). '' = not allowed. */
export function shortTargetPath(
  form: { type?: unknown; diplomaSlug?: unknown; key?: unknown; slug?: unknown; isPrimary?: unknown },
  path: ShortLinkPath | string,
): string {
  const f = obj(form);
  if (formTypeOf(f) === 'general') {
    const slug = typeof f.slug === 'string' ? f.slug : '';
    return path === 'form' && SHORT_SLUG_RE.test(slug) ? generalPath(slug) : '';
  }
  const ds = typeof f.diplomaSlug === 'string' ? f.diplomaSlug : '';
  if (!SHORT_SLUG_RE.test(ds)) return '';
  if (path === 'main') return f.isPrimary === true ? applyPath(ds, null) : '';
  if (path !== 'form') return '';
  const key = typeof f.key === 'string' ? f.key : '';
  return FORM_KEY_RE.test(key) ? applyPath(ds, key) : '';
}
/** A locale-prefixed form path a short link may redirect to; nothing else (no host, no '//', no '..'). */
export function isShortTarget(p: string): boolean {
  return typeof p === 'string' && SHORT_TARGET_RE.test(p);
}
/** The dedup signature of an auto link (QD9). */
export function shortSig(formId: string, locale: string, targetPath: string, tags: Partial<ShortLinkTags> | null | undefined): string {
  return formId + '|' + locale + '|' + targetPath + '|' + shortTagQuery(tags);
}
/** Where an unknown code goes: the site home of the locale. */
export function goHome(locale: string): string {
  return locale === 'en' ? '/en/' : '/';
}
/** Bots, link-preview fetchers and scripted clients are never counted as a click. */
export function isGoBot(ua: string | null | undefined): boolean {
  return isBotUa(ua) || LINK_PREVIEW_UA_RE.test(String(ua));
}
/** A prefetch or prerender (Purpose / Sec-Purpose, Meta's X-Purpose: preview, X-Moz: prefetch) is never a click. */
export function isGoPrefetch(purpose: unknown, xPurpose: unknown, xMoz: unknown): boolean {
  return (typeof purpose === 'string' && /prefetch|prerender/i.test(purpose)) ||
    (typeof xPurpose === 'string' && /preview|prefetch/i.test(xPurpose)) ||
    (typeof xMoz === 'string' && /prefetch/i.test(xMoz));
}
/** Cut to `max` UTF-16 units without leaving a lone high surrogate at the end. */
function cutWhole(s: string, max: number): string {
  if (s.length <= max) return s;
  let out = s.slice(0, max);
  const last = out.charCodeAt(out.length - 1);
  if (last >= 0xd800 && last <= 0xdbff) out = out.slice(0, -1);
  return out;
}
/**
 * The redirect Location (Q.3.3, QD4): `target` + the stored tags + the visitor's own query. A non-empty stored
 * utm_<k> wins over the incoming one; every other incoming pair passes (printable ASCII byte-for-byte, anything else
 * re-encoded; malformed pieces dropped). Size: utm_* values cut to utmChars, long click ids / other pairs dropped,
 * then non-priority pairs from the end, then priority pairs in reverse GO_PRIORITY_KEYS order, until ≤ max.
 */
export function goLocation(target: string, tags: Partial<ShortLinkTags> | null | undefined, rawSearch: string,
  max?: number): string {
  const limit = typeof max === 'number' ? max : SHORT_LINK_LIMITS.location;
  const t = obj(tags);
  const stored = shortTagQuery(tags);
  const storedPairs = stored ? stored.split('&') : [];
  const incoming: { key: string; text: string }[] = [];
  let q = typeof rawSearch === 'string' ? rawSearch : '';
  const hash = q.indexOf('#');
  if (hash >= 0) q = q.slice(0, hash);
  if (q.charAt(0) === '?') q = q.slice(1);
  const pieces = q ? q.split('&') : [];
  for (let i = 0; i < pieces.length; i++) {
    const piece = pieces[i];
    if (!piece) continue;
    const eq = piece.indexOf('=');
    const rawKey = eq < 0 ? piece : piece.slice(0, eq);
    const rawVal = eq < 0 ? '' : piece.slice(eq + 1);
    if (!rawKey) continue;
    const key = safeDecode(rawKey);
    const val = safeDecode(rawVal);
    if (key === null || val === null || key === '') continue;
    const m = /^utm_(source|medium|campaign|content|term)$/.exec(key);
    if (m && typeof t[m[1]] === 'string' && t[m[1]] !== '') continue;
    let text: string | null;
    if (/^utm_/.test(key) && val.length > SHORT_LINK_LIMITS.utmChars) {
      const ek = safeEncode(key);
      const ev = safeEncode(cutWhole(val, SHORT_LINK_LIMITS.utmChars));
      text = ek === null || ev === null ? null : ek + '=' + ev;
    } else if (PRINTABLE_PAIR_RE.test(piece)) {
      text = piece;
    } else {
      const ek = safeEncode(key);
      const ev = safeEncode(val);
      text = ek === null || ev === null ? null : ek + (eq < 0 ? '' : '=') + ev;
    }
    if (text === null) continue;
    if (contains(GO_CLICK_ID_KEYS, key)) {
      if (val.length > SHORT_LINK_LIMITS.clickIdChars) continue;
    } else if (!/^utm_/.test(key) && text.length > SHORT_LINK_LIMITS.pairOther) {
      continue;
    }
    incoming.push({ key, text });
  }
  const size = (): number => {
    let n = target.length + 1;
    for (let i = 0; i < storedPairs.length; i++) n += storedPairs[i].length + 1;
    for (let i = 0; i < incoming.length; i++) n += incoming[i].text.length + 1;
    return n;
  };
  for (let i = incoming.length - 1; i >= 0 && size() > limit; i--) {
    if (!contains(GO_PRIORITY_KEYS, incoming[i].key)) incoming.splice(i, 1);
  }
  for (let k = GO_PRIORITY_KEYS.length - 1; k >= 0 && size() > limit; k--) {
    for (let i = incoming.length - 1; i >= 0; i--) {
      if (incoming[i].key === GO_PRIORITY_KEYS[k]) incoming.splice(i, 1);
    }
  }
  const pairs = storedPairs.slice();
  for (let i = 0; i < incoming.length; i++) pairs.push(incoming[i].text);
  return target + (pairs.length ? '?' + pairs.join('&') : '');
}

// ---- additions (R-01, 7 Oct: almost leads, SPEC-R) ----
// Visitors who typed a valid phone and left without pressing Send (R.1.2): the per-form switch and notice, the
// contact phone question, the per-answer validation of a partial capture and where the visitor stopped (the funnel's
// own stopIndex rule, so the inbox and the funnel never disagree). Pure: the BE stores, the FE sends; this decides.
import { PARTIAL_LIMITS } from './leadform.contract';
import type { AlmostSettings, PartialStop } from './leadform.contract';

export const DEFAULT_PARTIAL_NOTICE_AR = 'بإدخال رقمك توافق على أن نتواصل معك بخصوص هذا البرنامج.';
export const DEFAULT_PARTIAL_NOTICE_EN = 'By entering your number you agree we may contact you about this program.';

/** {on (default true), text, text_ar} ('' text = the default of that locale). */
export function normalizeAlmost(raw: unknown): AlmostSettings {
  const o = obj(raw);
  return { on: bool(o.on, true), text: str(o.text, PARTIAL_LIMITS.notice), text_ar: str(o.text_ar, PARTIAL_LIMITS.notice) };
}
/** The form's settings: `common.almost`, or capture on with the default texts when the builder never set them. */
export function almostOf(common: Partial<LeadFormCommon> | null | undefined): AlmostSettings {
  const c = obj(common);
  return isObj(c.almost) ? normalizeAlmost(c.almost) : { on: true, text: '', text_ar: '' };
}
/** The notice under the phone field in `locale` (the builder's text, else the default of that locale). */
export function partialNoticeText(common: Partial<LeadFormCommon> | null | undefined, locale: string): string {
  const a = almostOf(common);
  return locale === 'ar' ? a.text_ar || DEFAULT_PARTIAL_NOTICE_AR : a.text || DEFAULT_PARTIAL_NOTICE_EN;
}
/** The visitor's own number: the first phone question in page order (`questions` is the page order), null when none. */
export function contactPhoneQuestion(variant: Pick<LeadFormVariant, 'questions'> | null | undefined): LeadFormQuestion | null {
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  for (let i = 0; i < qs.length; i++) if (qs[i] && qs[i].type === 'phone') return qs[i];
  return null;
}
/** An almost-lead can be captured on this form: capture is on and the variant asks for a phone. */
export function canCapturePartial(common: Partial<LeadFormCommon> | null | undefined,
  variant: Pick<LeadFormVariant, 'questions'> | null | undefined): boolean {
  return almostOf(common).on && !!contactPhoneQuestion(variant);
}
/**
 * The answers so far of a partial capture, in page order: one clean answer (label snapshots, as validateAnswers) for
 * each question whose raw value is non-empty and passes validateAnswer; a bad answer is dropped alone, an unknown qid
 * is ignored, an optional malformed email keeps its text with v null (P.1.6). null when `raw` is not an object or has
 * more keys than the variant has questions.
 */
export function partialAnswers(variant: Pick<LeadFormVariant, 'questions'> | null | undefined, raw: unknown): LeadAnswer[] | null {
  if (!isObj(raw)) return null;
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  const given = raw as Obj;
  let count = 0;
  for (const k in given) if (own(given, k)) count++;
  if (count > qs.length) return null;
  const out: LeadAnswer[] = [];
  for (let i = 0; i < qs.length; i++) {
    const q = qs[i];
    if (!own(given, q.id)) continue;
    const v0 = given[q.id];
    if (isEmptyAnswer(v0) || validateAnswer(q, v0) !== '') continue;
    const v = cleanValue(q, v0);
    const text = q.type === 'email' && v === null && typeof v0 === 'string' ? v0.trim().slice(0, 254) : answerText(q, v);
    out.push({ q: q.id, t: q.type, label: q.label, label_ar: q.label_ar, v, text });
  }
  return out;
}
/** One char per question in page order: 'a' answered (v not null), 'r' seen, '-' otherwise (the funnel alphabet). */
export function partialPattern(variant: Pick<LeadFormVariant, 'questions'> | null | undefined, clean: LeadAnswer[] | null | undefined,
  seen: string[] | null | undefined): string {
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  const answered: Obj = {};
  const list = arr(clean) as LeadAnswer[];
  for (let i = 0; i < list.length; i++) if (list[i] && list[i].v !== null && list[i].v !== undefined) answered['~' + list[i].q] = 1;
  const s = arr(seen);
  let p = '';
  for (let i = 0; i < qs.length; i++) p += answered['~' + qs[i].id] ? 'a' : contains(s, qs[i].id) ? 'r' : '-';
  return p;
}
/** Where the visitor stopped (R.1.2): the funnel's stopIndex over partialPattern, plus the furthest seen, the last
 *  answered, the first required question left empty and the required counts (with none required: answered / all). */
export function partialStop(variant: { questions: LeadFormQuestion[]; sections?: LeadFormSection[] } | null | undefined,
  clean: LeadAnswer[] | null | undefined, seen: string[] | null | undefined, la: string): PartialStop {
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  const p = partialPattern(variant, clean, seen);
  let index = stopIndex(qs, p);
  if (index === -2) index = nextRequiredIndex({ questions: qs }, -1);
  let seenIndex = -1;
  let lastIndex = -1;
  let laIndex = -1;
  let firstEmpty = -1;
  let req = 0;
  let reqDone = 0;
  let answered = 0;
  for (let i = 0; i < qs.length; i++) {
    const c = p.charAt(i);
    if (c === 'a' || c === 'r') seenIndex = i;
    if (c === 'a') {
      answered++;
      lastIndex = i;
      if (qs[i].id === la) laIndex = i;
    }
    if (qs[i].required) {
      req++;
      if (c === 'a') reqDone++;
      else if (firstEmpty < 0) firstEmpty = i;
    }
  }
  if (laIndex >= 0) lastIndex = laIndex;
  const sq = index >= 0 ? qs[index] : null;
  const secId = sq ? sectionOf(variant, sq.id) : '';
  const secs = arr(variant && variant.sections) as LeadFormSection[];
  let sec: LeadFormSection | null = null;
  for (let i = 0; i < secs.length; i++) if (secs[i] && secs[i].id === secId) sec = secs[i];
  return {
    index,
    q: sq ? sq.id : '', label: sq ? sq.label : '', label_ar: sq ? sq.label_ar : '',
    section: secId, sectionTitle: sec ? sec.title || '' : '', sectionTitle_ar: sec ? sec.title_ar || '' : '',
    seenQ: seenIndex >= 0 ? qs[seenIndex].id : '', seenIndex,
    lastQ: lastIndex >= 0 ? qs[lastIndex].id : '', lastIndex,
    firstEmptyRequiredQ: firstEmpty >= 0 ? qs[firstEmpty].id : '', firstEmptyRequiredIndex: firstEmpty,
    requiredDone: req > 0 ? reqDone : answered, requiredTotal: req > 0 ? req : qs.length, answered, questions: qs.length,
  };
}
/** R8: of the left, started sessions that left a phone (agg.phonePatterns), those whose stop is -1 (every required
 *  question answered, did not press Send). null for layout 1 or without the branch. */
export function quitAtSubmitPhone(variant: Pick<LeadFormVariant, 'questions'> | null | undefined, agg: FunnelAgg | null | undefined): number | null {
  if (!agg || agg.layout !== 2 || !Array.isArray(agg.phonePatterns)) return null;
  const qs = arr(variant && variant.questions) as LeadFormQuestion[];
  const rows = agg.phonePatterns;
  let n = 0;
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (r && r.n > 0 && stopIndex(qs, r.p) === -1) n += r.n;
  }
  return n;
}
